import React, { Fragment, useState, useMemo, useRef, memo, useCallback } from "react";
import { BiCommentDetail } from "react-icons/bi";
import { FiChevronDown } from "react-icons/fi";
import sparklesIcon from "assets/sparkles.svg";
import { RiPencilFill } from "react-icons/ri";
import { ImClock } from "react-icons/im";
import { GoAlert } from "react-icons/go";
import { RxLockClosed } from "react-icons/rx";
import { EmployeeLink } from "components/Employees/EmployeeEditDrawer";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import PlanShiftHoverMenu from "components/Plans/PlanShiftHoverMenu";
import PlanPersonCard from "components/Plans/PlanPersonCard";
import ShiftObservationTooltip from "components/Plans/ShiftObservationTooltip";
import Avatar from "components/Avatar";
import { PLAN_GRID_VIEWS, supportsPlanLoadSkeletons } from "components/Plans";
import { resolveDayAssignments } from "utils/planWorkflowAbsences";
import {
	getInternalShiftPlanFromDropEvent,
	isInternalShiftPlanDrag,
	rowSelectionSignature,
} from "utils/planDaySelection";
import {
	darkenColor,
	getContrastColor,
	isLightColor,
	resolveShiftBackgroundColor,
} from "utils/utils";
import i18next from "i18next";
import {
	formatSecondsToTime,
	calculateWorkTime,
	fixStartEndIntervals,
	getFirstLastInterval,
} from "utils/datetime";
import { convertFromUTC } from "utils/datetime";
import { getMultigroupSectionVars } from "utils/multigroupPlanGrouping";
import moment from "moment";
import { shiftType, normalizeShiftType } from "services/shiftsService";
import {
	buildResizePointerHandlers,
	filterPreviewIntervalsByKind,
	formatResizePreviewLabel,
	getHourBlockOverlapForResizePreview,
	getIntervalIndexForResizeHandle,
	getLocalIntervalDurationMinutes,
	getHourBlockOverlapWithLocalInterval,
	isResizeableHourlyAssignment,
	isAvailabilityTimeInterval,
	mergeDayHoursForResizePreview,
	enrichHourForResizePreview,
	getPreviewForPlanDay,
	dropRestWhenShiftPaintsHour,
	hasPartialDayIntervals,
	isFullDayTimeInterval,
	cardIntervalsOfAssignment,
	HOURLY_MOVE_EDGE,
} from "components/Plans/hourlyShiftResize";

// fixStartEndIntervals ahora se importa desde utils/datetime

const isWorkLikeShift = (type) => {
	const normalized = normalizeShiftType(type);
	return (
		normalized === shiftType.work || normalized === shiftType.automatic
	);
};
const isAutomaticShift = (type) =>
	normalizeShiftType(type) === shiftType.automatic;

/** Contorno de periodos con fondo blanco/claro en vista horas */
const LIGHT_PERIOD_BORDER = "#666666";
const LIGHT_PERIOD_BORDER_HOVER = "#333333";
const LIGHT_PERIOD_BORDER_WIDTH = 1;

const isAbsenceShift = (type) =>
	normalizeShiftType(type) === shiftType.absence;

const isVacationOrAbsenceShift = (type) => {
	const normalized = normalizeShiftType(type);
	return (
		normalized === shiftType.vacation || normalized === shiftType.absence
	);
};

const shouldShowShiftHoursLayout = (shift, time_intervals) =>
	isWorkLikeShift(shift?.type) ||
	(isVacationOrAbsenceShift(shift?.type) &&
		hasPartialDayIntervals(time_intervals));

const PLAN_ABSENCE_DESCRIPTION_MAX_LEN = 18;
const truncateShiftDescription = (
	description,
	maxLen = PLAN_ABSENCE_DESCRIPTION_MAX_LEN
) => {
	if (!description || description.length <= maxLen) return description;
	return `${description.substring(0, maxLen)}…`;
};

const renderAbsenceShiftLabel = (shift, style = {}) => (
	<div className="shift-absence-label" style={style}>
		<span className="shift-absence-name">{shift.name}</span>
		{shift.description && (
			<span
				className="shift-absence-description substring"
				title={shift.description}
			>
				{truncateShiftDescription(shift.description)}
			</span>
		)}
	</div>
);

/** Evita que el badge de función pise el periodo en celdas estrechas. */
const PLAN_FUNCTION_BADGE_MAX_LEN = 7;
const truncatePlanFunctionLabel = (name, maxLen = PLAN_FUNCTION_BADGE_MAX_LEN) => {
	if (!name || name.length <= maxLen) return name;
	return `${name.substring(0, maxLen)}…`;
};

const isAvailabilityInterval = (interval) =>
	interval.time_type?.name === "Disponibilidad" ||
	interval.time_type?.work_time === false;

const isWorkInterval = (interval) => interval.time_type?.work_time === true;

/** Disponibilidad vs trabajo según time_type de los intervalos efectivos (plan o turno). */
const getIntervalsDisplayMode = (time_intervals) => {
	if (!time_intervals?.length) return null;
	if (time_intervals.some(isWorkInterval)) return "work";
	if (time_intervals.some(isAvailabilityInterval)) return "availability";
	return null;
};

const calculateIntervalsDuration = (intervals) => {
	if (!intervals?.length) return 0;

	let totalSeconds = 0;
	intervals.forEach((interval) => {
		const localStart = convertFromUTC(interval.start);
		const localEnd = convertFromUTC(interval.end);
		const [startHours, startMinutes] = localStart.split(":").map(Number);
		const [endHours, endMinutes] = localEnd.split(":").map(Number);
		let startInSeconds = startHours * 3600 + startMinutes * 60;
		let endInSeconds = endHours * 3600 + endMinutes * 60;

		if (endInSeconds < startInSeconds) {
			endInSeconds += 24 * 3600;
		}

		totalSeconds += endInSeconds - startInSeconds;
	});

	return totalSeconds;
};

const getDisplayIntervals = (time_intervals, shiftType) => {
	if (!time_intervals?.length) return [];

	if (isVacationOrAbsenceShift(shiftType)) {
		return time_intervals.filter(
			(interval) => !isFullDayTimeInterval(interval)
		);
	}

	const mode = getIntervalsDisplayMode(time_intervals);
	if (mode === "availability") {
		return time_intervals.filter(isAvailabilityInterval);
	}
	if (mode === "work") {
		return time_intervals.filter(isWorkInterval);
	}

	if (isAutomaticShift(shiftType)) {
		return time_intervals;
	}

	return time_intervals.filter((interval) => interval.time_type?.work_time);
};

const getShiftDisplayTotal = (time_intervals, shiftType) => {
	const intervals = getDisplayIntervals(time_intervals, shiftType);

	if (!intervals.length) {
		return formatSecondsToTime(0, "hm");
	}

	if (
		getIntervalsDisplayMode(time_intervals) === "availability" ||
		isVacationOrAbsenceShift(shiftType)
	) {
		return formatSecondsToTime(calculateIntervalsDuration(intervals), "hm");
	}

	return formatSecondsToTime(calculateWorkTime(time_intervals), "hm");
};

const getShiftIntervalsForDisplay = (time_intervals, color, shiftType) =>
	getDisplayIntervals(time_intervals, shiftType).map((item, i) => {
		const start = convertFromUTC(item.start);
		const end = convertFromUTC(item.end);

		return (
			<li key={i} className="time-interval" style={{ borderColor: color }}>
				{start}-{end}h
			</li>
		);
	});

/** Badge IA gris = intervalos de Disponibilidad en la asignación (aún no convertida). */
const shouldShowIaBadge = (time_intervals, shift) => {
	if (isVacationOrAbsenceShift(shift?.type)) return false;
	return getIntervalsDisplayMode(time_intervals) === "availability";
};

/**
 * Badge IA primary = assignment de trabajo creado por el plan inteligente.
 *
 * Hay dos patrones según el tipo de turno que asigna la IA:
 *
 * 1. Turno Automático (TA): el planificador convierte disponibilidades a trabajo.
 *    Los intervalos siempre se almacenan en la asignación (time_intervals_from_manual=true
 *    es el estado NORMAL para este tipo de turno, no indica edición del usuario).
 *    Señal de IA: locked=true.
 *
 * 2. Turno Trabajo: el planificador asigna un turno fijo.
 *    Los intervalos vienen del template del turno (time_intervals_from_shift=true).
 *    Se excluye si el usuario editó los intervalos (time_intervals_from_manual=true).
 *    Señal de IA: locked=true.
 *
 * En ambos casos la rueda de turnos asigna con locked=false.
 */
const shouldShowIaBadgePrimary = (shift, assignment, time_intervals) => {
	if (isVacationOrAbsenceShift(shift?.type)) return false;
	if (getIntervalsDisplayMode(time_intervals) !== "work") return false;
	if (isAutomaticShift(shift.type)) {
		// Para turno Automático la IA siempre almacena los intervalos de trabajo
		// en la asignación (time_intervals_from_manual=true): es la señal de que fue
		// el planificador inteligente quien convirtió la disponibilidad a trabajo.
		// La rueda de turnos no persiste intervalos propios, así que
		// time_intervals_from_manual queda false.
		// Usamos este campo en vez de locked para que el badge persista tras desbloquear.
		return !!assignment.time_intervals_from_manual;
	}
	// Para turno Trabajo: señal de IA = locked + intervalos del template del turno,
	// sin editar manualmente. Las asignaciones manuales (sidebar) también van con
	// locked=true (exigencia del API) pero time_intervals_from_shift queda false.
	if (!assignment.locked) return false;
	if (!assignment.time_intervals_from_shift) return false;
	return !assignment.time_intervals_from_manual;
};


const renderShiftBadge = (shift, item, time_intervals, options = {}) => {
	const { stacked = false } = options;
	const alertMargin =
		item.alert_min_rest_hours || item.alert_max_consecutive_working_days
			? { marginRight: "10px" }
			: {};

	const shiftLabel = shift.name.substring(0, 3);
	const groupClassName = stacked
		? "shift-badge-group shift-badge-group--stacked"
		: "shift-badge-group";

	if (shouldShowIaBadgePrimary(shift, item, time_intervals)) {
		return (
			<div
				className={groupClassName}
				title={shift.name}
				style={alertMargin}
			>
				<div className="shift-badge">{shiftLabel}</div>
				<span className="shift-badge shift-badge--ia">
					<img
						src={sparklesIcon}
						alt=""
						className="shift-badge--ia-icon"
						aria-hidden="true"
					/>
					IA
				</span>
			</div>
		);
	}

	if (shouldShowIaBadge(time_intervals, shift)) {
		return (
			<div
				className={groupClassName}
				title={shift.name}
				style={alertMargin}
			>
				<div className="shift-badge">{shiftLabel}</div>
				<span className="shift-badge shift-badge--ia shift-badge--ia--availability">
					<img
						src={sparklesIcon}
						alt=""
						className="shift-badge--ia-icon"
						aria-hidden="true"
					/>
					IA
				</span>
			</div>
		);
	}

	return (
		<div className="shift-badge" title={shift.name} style={alertMargin}>
			{shiftLabel}
		</div>
	);
};

/** Badge IA para PlanPersonCard (sin abreviatura del turno). */
const renderPersonIaBadge = (shift, item, time_intervals) => {
	if (shouldShowIaBadgePrimary(shift, item, time_intervals)) {
		return (
			<span className="shift-badge shift-badge--ia">
				<img
					src={sparklesIcon}
					alt=""
					className="shift-badge--ia-icon"
					aria-hidden="true"
				/>
				IA
			</span>
		);
	}

	if (shouldShowIaBadge(time_intervals, shift)) {
		return (
			<span className="shift-badge shift-badge--ia shift-badge--ia--availability">
				<img
					src={sparklesIcon}
					alt=""
					className="shift-badge--ia-icon"
					aria-hidden="true"
				/>
				IA
			</span>
		);
	}

	return null;
};

// Helper para convertir color hex a rgba con opacidad
const hexToRgba = (hex, alpha = 1) => {
	if (!hex) return `rgba(0, 0, 0, ${alpha})`;

	// Remover el # si existe
	hex = hex.replace("#", "");

	// Convertir a RGB
	let r, g, b;
	if (hex.length === 3) {
		r = parseInt(hex[0] + hex[0], 16);
		g = parseInt(hex[1] + hex[1], 16);
		b = parseInt(hex[2] + hex[2], 16);
	} else {
		r = parseInt(hex.substring(0, 2), 16);
		g = parseInt(hex.substring(2, 4), 16);
		b = parseInt(hex.substring(4, 6), 16);
	}

	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Helper para detectar si un color es muy claro (casi blanco)
const isVeryLightColor = (hex) => {
	if (!hex) return false;

	// Remover el # si existe
	const cleanHex = hex.replace("#", "");

	// Convertir a RGB
	let r, g, b;
	if (cleanHex.length === 3) {
		r = parseInt(cleanHex[0] + cleanHex[0], 16);
		g = parseInt(cleanHex[1] + cleanHex[1], 16);
		b = parseInt(cleanHex[2] + cleanHex[2], 16);
	} else if (cleanHex.length === 6) {
		r = parseInt(cleanHex.substring(0, 2), 16);
		g = parseInt(cleanHex.substring(2, 4), 16);
		b = parseInt(cleanHex.substring(4, 6), 16);
	} else {
		// Si el formato no es válido, retornar false
		return false;
	}

	// Verificar que los valores sean números válidos
	if (isNaN(r) || isNaN(g) || isNaN(b)) {
		return false;
	}

	// Calcular luminosidad usando la fórmula estándar
	// https://www.w3.org/TR/WCAG20/#relativeluminancedef
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	// Considerar muy claro si la luminosidad es mayor a 0.9 (muy cerca del blanco)
	return luminance > 0.9;
};

/**
 * Intervalos efectivos para UI. La vista Horas usa el span (días encadenados).
 * La tarjeta de un día no: si no, un 00:00-00:00 seguido pinta un periodo por día.
 */
const getAssignmentRawIntervals = (assignment, { includeSpan = true } = {}) => {
	if (!assignment) return null;
	if (includeSpan && assignment.time_intervals_span?.length) {
		return assignment.time_intervals_span;
	}
	return cardIntervalsOfAssignment(assignment);
};

const DAY_CARD_INTERVALS = { includeSpan: false };

const blockContextMenu = (e) => {
	e.preventDefault();
	e.stopPropagation();
};

const onShiftItemDayClick = (props, row, index, e) => {
	e.stopPropagation();
	if (props.hasPrivileges) {
		props.handleDaySelection(row, index, e);
	}
};

const hasShiftComments = (item) =>
	typeof item?.comments === "string" && item.comments.trim() !== "";

const showInlineShiftComment = (item, shift) =>
	hasShiftComments(item) &&
	shift?.id !== undefined &&
	isWorkLikeShift(shift.type);

const renderShiftCommentIcon = (className = "shift-comment-icon") => (
	<BiCommentDetail className={className} aria-hidden="true" />
);

const renderShiftTotalWithComment = (total, item, shift) => (
	<div className="shift-total-row">
		<div className="total-badge">
			{total}
			{showInlineShiftComment(item, shift) && renderShiftCommentIcon()}
		</div>
	</div>
);

const renderShiftObservationTooltip = (
	item,
	hoveredAssignmentId,
	key,
	isDragging
) =>
	hoveredAssignmentId === key &&
	!isDragging &&
	hasShiftComments(item) && (
		<ShiftObservationTooltip comments={item.comments} />
	);

const getDefaultAssignedShifts = (
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	employee,
	sp_item,
	row,
	index
) =>
	resolveDayAssignments(
		sp_item.assignments,
		props.default_shift,
		true
	).map((item) => {
		const shift = item.shift;
		const funcion = item.function;

		const key = item.id === -1 ? `empty-${row}-${index}` : item.id; // Usamos el id de la asignación como key
		const editedTimes =
			item.time_intervals_from_manual &&
			item.time_intervals &&
			item.time_intervals.length > 0;
		const day_selection = props.day_selection;
		const plan = { employee: employee, day: sp_item.day, shift_plan: item };
		const shiftname = shift.description
			? shift.name + " - " + shift.description
			: shift.name;
		const tooltip_alerta = (
			<Tooltip className="tooltip-alerta">
				<ul>
					{item.alert_min_rest_hours && (
						<li>
							<GoAlert className="tooltip-icon icon-alert" /> No se cumple el
							mínimo de horas entre turnos
						</li>
					)}
					{item.alert_max_consecutive_working_days && (
						<li>
							<GoAlert className="tooltip-icon icon-alert" /> No se cumple el
							máximo de días de trabajo consecutivos
						</li>
					)}
				</ul>
			</Tooltip>
		);
		const raw_time_intervals = getAssignmentRawIntervals(item, DAY_CARD_INTERVALS);
		const time_intervals = fixStartEndIntervals(raw_time_intervals);
		const total = time_intervals
			? getShiftDisplayTotal(time_intervals, shift.type)
			: formatSecondsToTime(0, "hm");

		// Normalizar colores para comparación (lowercase y sin espacios)
		const normalizedBorderColor = shift.border_color
			? shift.border_color.toLowerCase().trim()
			: null;
		const normalizedBackgroundColor = shift.background_color
			? shift.background_color.toLowerCase().trim()
			: null;
		const useSameBorderAsBackground =
			!shift.border_color ||
			normalizedBorderColor === normalizedBackgroundColor;

		const borderColor = shift.border_color || shift.background_color;
		const finalBorderColor = useSameBorderAsBackground
			? hexToRgba(borderColor, 0.05)
			: borderColor;

	const handleShiftClick = (e) => {
			if (!props.hasPrivileges) return;
			e.stopPropagation();
			const intervalsMode = getIntervalsDisplayMode(time_intervals);
			console.group(
				`[Turno Seleccionado] "${shift.name}" | empleado: ${employee.name} ${employee.surname} | día: ${sp_item.day}`
			);
			console.log("── Identificadores ──────────────────────────────────");
			console.log("  assignment.id:", item.id);
			console.log("  shift.id:", shift.id, "| shift.name:", shift.name, "| shift.type:", shift.type);
			console.log("── Flags de origen (IA vs rueda vs manual) ──────────");
			console.log("  locked:", item.locked, "← true=asignación manual (API) o IA; false=rueda de turnos");
			console.log("  time_intervals_from_manual:", item.time_intervals_from_manual, "← true=editado por usuario (en T.Trabajo oculta badge IA)");
			console.log("  time_intervals_from_shift:", item.time_intervals_from_shift, "← true=intervalos del template (requerido para badge IA en T.Trabajo)");
			console.log("  time_intervals_from_override_time_registrations:", item.time_intervals_from_override_time_registrations);
			console.log("── Intervalos ───────────────────────────────────────");
			console.log("  intervalsMode (work/availability/null):", intervalsMode);
			console.log("  time_intervals (raw, tras fixStartEnd):", time_intervals);
			console.log("  shift.time_intervals (template turno):", shift.time_intervals);
			console.log("── Badge IA ─────────────────────────────────────────");
			console.log("  shouldShowIaBadgePrimary:", shouldShowIaBadgePrimary(shift, item, time_intervals));
			console.log("  shouldShowIaBadge (gris):", shouldShowIaBadge(time_intervals, shift));
			console.log("── Función asignada ─────────────────────────────────");
			console.log("  function:", item.function ? `id=${item.function.id} name="${item.function.name}"` : null);
			console.log("── Alertas / extras ─────────────────────────────────");
			console.log("  alert_min_rest_hours:", item.alert_min_rest_hours);
			console.log("  alert_max_consecutive_working_days:", item.alert_max_consecutive_working_days);
			console.log("  comments:", item.comments);
			console.log("  ubications:", item.ubications);
			console.log("── Objeto completo de la asignación ─────────────────");
			console.log("  assignment (item):", item);
			console.log("  shift (completo):", shift);
			console.groupEnd();
			props.handleDaySelection(row, index, e);
		};

	return (
		<li
			key={key}
			title={`${shiftname}`}
			className="shift-item shift-item-intervals"
			style={{
				// backgroundColor: shift.background_color && hexToRgba(shift.background_color, 0.3),
				backgroundColor: shift.background_color,
				borderColor: finalBorderColor,
				color: getContrastColor(shift.background_color),
				cursor: props.hasPrivileges ? "grab" : "default",
				borderRadius: "4px",
			}}
			draggable={props.hasPrivileges}
			onDragStart={(e) => {
				if (!props.hasPrivileges) {
					e.preventDefault(); // Cancela el drag completamente
					return;
				}

				setIsDragging(true);
				onShiftDragStart(e, plan);
			}}
			onDragEnd={(e) => {
				setIsDragging(false);
				// Limpiar atributo de debugging
				e.target.removeAttribute("data-drag-plan");
			}}
			onClick={handleShiftClick}
			onMouseEnter={() => {
				if (!isDragging && props.hasPrivileges) {
					//setTimeout(() => setHoveredAssignmentId(key), 5); // Retraso pequeño
					setHoveredAssignmentId(key);
				}
			}}
			onMouseLeave={() => {
				if (!isDragging && props.hasPrivileges && !props.isAddingShift) {
					setHoveredAssignmentId(null);
					//setTimeout(() => setHoveredAssignmentId(null), 5); // Evita interrupciones
				}
			}}
		>
			<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
				{/* {item.locked && <RxLockClosed className="plan-icon icon-locked" style={{color: "black"}} size={16} />} */}
				{item.locked && (
					<RxLockClosed className="plan-icon icon-locked" size={16} />
				)}
					{item.time_intervals_from_override_time_registrations && (
						<ImClock className="plan-icon icon-clock" />
					)}
					{hasShiftComments(item) &&
						!showInlineShiftComment(item, shift) &&
						renderShiftCommentIcon("plan-icon icon-comments")}
					{(item.alert_min_rest_hours ||
						item.alert_max_consecutive_working_days) && (
						<OverlayTrigger overlay={tooltip_alerta}>
							<GoAlert className="plan-icon icon-alert" />
						</OverlayTrigger>
					)}
					{editedTimes && <RiPencilFill className="plan-icon icon-edited" />}

					{shift.id === props.default_shift?.id && (
						<span
							style={
								item.alert_min_rest_hours ||
								item.alert_max_consecutive_working_days
									? { marginRight: "10px" }
									: {}
							}
						>
							{i18next.t("shifts.form.typeRest")}
						</span>
					)}
					{shift.id !== props.default_shift?.id &&
						!shouldShowShiftHoursLayout(shift, time_intervals) &&
						(isAbsenceShift(shift.type) ? (
							renderAbsenceShiftLabel(shift, {
								...(item.alert_min_rest_hours ||
								item.alert_max_consecutive_working_days
									? { marginRight: "10px" }
									: {}),
							})
						) : (
							<span
								style={
									item.alert_min_rest_hours ||
									item.alert_max_consecutive_working_days
										? { marginRight: "10px" }
										: {}
								}
							>
								{shift.name}
							</span>
						))}

					{shift.id !== props.default_shift?.id &&
						shouldShowShiftHoursLayout(shift, time_intervals) && (
						<div className="shift-item-content">
							<div className="shift-info-left">
								{time_intervals && time_intervals.length > 0 && (
									<ul className="time-intervals">
										{getShiftIntervalsForDisplay(
											time_intervals,
											shift.background_color,
											shift.type
										)}
									</ul>
								)}
								{renderShiftTotalWithComment(total, item, shift)}
							</div>

							<div className="shift-info-right">
								{(item.function || isWorkLikeShift(shift.type)) &&
									(item.function ? (
									<span
										title={item.function.name}
										className="shift-function substring"
										style={{
											backgroundColor: item.function.background_color
												? item.function.background_color
												: !shift.background_color ||
												  isVeryLightColor(shift.background_color)
												? "#000000"
												: shift.background_color,
											borderColor: item.function.background_color
												? item.function.background_color
												: !shift.background_color ||
												  isVeryLightColor(shift.background_color)
												? "#000000"
												: shift.border_color
												? shift.border_color
												: shift.background_color,
											color: item.function.background_color
												? getContrastColor(item.function.background_color)
												: !shift.background_color ||
												  isVeryLightColor(shift.background_color)
												? "#FFFFFF"
												: getContrastColor(shift.background_color),
										}}
									>
										{truncatePlanFunctionLabel(item.function.name)}
									</span>
								) : (
									<span
										title={i18next.t('plan.noFunction')}
										className="shift-function substring"
										style={{
											backgroundColor:
												!shift.background_color ||
												isVeryLightColor(shift.background_color)
													? "#000000"
													: shift.background_color,
											borderColor:
												!shift.background_color ||
												isVeryLightColor(shift.background_color)
													? "#000000"
													: shift.border_color
													? shift.border_color
													: shift.background_color,
											color:
												!shift.background_color ||
												isVeryLightColor(shift.background_color)
													? "#FFFFFF"
													: getContrastColor(shift.background_color),
										}}
									>
										Sin función
									</span>
								))}
								{renderShiftBadge(shift, item, time_intervals)}
							</div>
						</div>
					)}
				</div>

				{renderShiftObservationTooltip(
					item,
					hoveredAssignmentId,
					key,
					isDragging
				)}
				{/* Renderizamos el menú de hover solo si este assignment está en hover */}
				{hoveredAssignmentId === key && !isDragging && (
					<PlanShiftHoverMenu
						plan={plan}
						defaultShiftId={props.default_shift?.id}
						onAdd={(update_employee_ids) =>
							props.updateEmployeeIdsAndGetAllAssignments(update_employee_ids)
						}
						onShiftAssigned={props.onShiftAssigned}
						onEdit={() => props.handleEditShift(plan)}
						onDelete={() => props.handleRemoveShift(plan, plan.shift_plan.id)}
					/>
				)}
			</li>
		);
	});

const getGridAssignedShifts = (
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	employee,
	sp_item,
	row,
	index
) =>
	resolveDayAssignments(
		sp_item.assignments,
		props.default_shift,
		true
	).map((item) => {
		const shift = item.shift;
		const editedTimes =
			item.time_intervals_from_manual &&
			item.time_intervals &&
			item.time_intervals.length > 0;
		const day_selection = props.day_selection;
		const plan = {
			employee: employee,
			day: sp_item.day,
			shift_plan: item,
		};
		const key = item.id === -1 ? `empty-${row}-${index}` : item.id; // Usamos el id de la asignación como key
		const shiftname = shift.description
			? shift.name + " - " + shift.description
			: shift.name;
		const tooltip_alerta = (
			<Tooltip className="tooltip-alerta">
				<ul>
					{item.alert_min_rest_hours && (
						<li>
							<GoAlert className="tooltip-icon icon-alert" /> No se cumple el
							mínimo de horas entre turnos
						</li>
					)}
					{item.alert_max_consecutive_working_days && (
						<li>
							<GoAlert className="tooltip-icon icon-alert" /> No se cumple el
							máximo de días de trabajo consecutivos
						</li>
					)}
				</ul>
			</Tooltip>
		);

		// Normalizar colores para comparación (lowercase y sin espacios)
		const normalizedBorderColor = shift.border_color
			? shift.border_color.toLowerCase().trim()
			: null;
		const normalizedBackgroundColor = shift.background_color
			? shift.background_color.toLowerCase().trim()
			: null;
		const useSameBorderAsBackground =
			!shift.border_color ||
			normalizedBorderColor === normalizedBackgroundColor;

		const borderColor = shift.border_color || shift.background_color;
		const finalBorderColor = useSameBorderAsBackground
			? hexToRgba(borderColor, 0.2)
			: borderColor;
		const raw_time_intervals = getAssignmentRawIntervals(item, DAY_CARD_INTERVALS);
		const time_intervals = fixStartEndIntervals(raw_time_intervals);

		return (
			<li
				key={item.id}
				title={`${shiftname}`}
				className="shift-item"
				style={{
					// backgroundColor: shift.background_color && hexToRgba(shift.background_color, 0.3),
					backgroundColor: shift.background_color,
					borderColor: finalBorderColor,
					color: getContrastColor(shift.background_color),
					cursor: props.hasPrivileges ? "grab" : "default",
					borderRadius: "4px",
				}}
				draggable={props.hasPrivileges}
				onDragStart={(e) => {
					if (!props.hasPrivileges) {
						e.preventDefault(); // Cancela el drag completamente
						return;
					}

					setIsDragging(true);
					onShiftDragStart(e, plan);
				}}
				onDragEnd={(e) => {
					setIsDragging(false);
					// Limpiar atributo de debugging
					e.target.removeAttribute("data-drag-plan");
				}}
				onClick={(e) => onShiftItemDayClick(props, row, index, e)}
				onMouseEnter={() => {
					if (!isDragging && props.hasPrivileges) {
						setTimeout(() => setHoveredAssignmentId(key), 5);
						//setHoveredAssignmentId(key)
					}
				}}
				onMouseLeave={() => {
					if (!isDragging && props.hasPrivileges && !props.isAddingShift) {
						// setTimeout(() => setHoveredAssignmentId(null), 5);
						setHoveredAssignmentId(null);
					}
				}}
			>
				<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
					{/* {item.locked && <RxLockClosed className="plan-icon icon-locked-simple" style={{color: "black"}} size={16} />} */}
					{item.locked && (
						<RxLockClosed className="plan-icon icon-locked-simple" size={16} />
					)}
					{item.time_intervals_from_override_time_registrations && (
						<ImClock className="plan-icon icon-clock" />
					)}
					{hasShiftComments(item) &&
						renderShiftCommentIcon("plan-icon icon-comments")}
					{(item.alert_min_rest_hours ||
						item.alert_max_consecutive_working_days) && (
						<OverlayTrigger overlay={tooltip_alerta}>
							<GoAlert className="plan-icon icon-alert" />
						</OverlayTrigger>
					)}
					{editedTimes && (
						<RiPencilFill className="plan-icon icon-edited-simple" />
					)}

					{shift.id !== props.default_shift?.id
						? renderShiftBadge(shift, item, time_intervals, { stacked: true })
						: "-"}
				</div>

				{renderShiftObservationTooltip(
					item,
					hoveredAssignmentId,
					key,
					isDragging
				)}
				{hoveredAssignmentId === key && !isDragging && (
					<PlanShiftHoverMenu
						plan={plan}
						defaultShiftId={props.default_shift?.id}
						onAdd={(update_employee_ids) =>
							props.updateEmployeeIdsAndGetAllAssignments(update_employee_ids)
						}
						onShiftAssigned={props.onShiftAssigned}
						onEdit={() => props.handleEditShift(plan)}
						onDelete={() => props.handleRemoveShift(plan, plan.shift_plan.id)}
					/>
				)}
			</li>
		);
	});

const getShiftIntervals = (time_intervals, color, shiftType = "Trabajo") =>
	getShiftIntervalsForDisplay(time_intervals, color, shiftType);

// celdas grid combo turnos + ubicaciones
const getAssignedShiftsWithUbications = (
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	employee,
	sp_item,
	row,
	index
) =>
	resolveDayAssignments(
		sp_item.assignments,
		props.default_shift,
		true
	).map((item) => {
		const shift = item.shift;
		const day_selection = props.day_selection;
		const plan = {
			employee: employee,
			day: sp_item.day,
			shift_plan: item,
		};
		const key = item.id === -1 ? `empty-${row}-${index}` : item.id;
		// time intervals editados
		const editedTimes =
			item.time_intervals &&
			item.time_intervals.find((item) => item.time_type.work_time === false);
		const shiftname = shift.description
			? shift.name + " - " + shift.description
			: shift.name;

		const raw_time_intervals = getAssignmentRawIntervals(item, DAY_CARD_INTERVALS);
		const time_intervals = fixStartEndIntervals(raw_time_intervals);
		const total = time_intervals
			? getShiftDisplayTotal(time_intervals, shift.type)
			: formatSecondsToTime(0, "hm");

		const tooltip_alerta = (
			<Tooltip className="tooltip-alerta">
				<ul>
					{item.alert_min_rest_hours && (
						<li>
							<GoAlert className="tooltip-icon icon-alert" /> No se cumple el
							mínimo de horas entre turnos
						</li>
					)}
					{item.alert_max_consecutive_working_days && (
						<li>
							<GoAlert className="tooltip-icon icon-alert" /> No se cumple el
							máximo de días de trabajo consecutivos
						</li>
					)}
				</ul>
			</Tooltip>
		);

		// Normalizar colores para comparación (lowercase y sin espacios)
		const normalizedBorderColor = shift.border_color
			? shift.border_color.toLowerCase().trim()
			: null;
		const normalizedBackgroundColor = shift.background_color
			? shift.background_color.toLowerCase().trim()
			: null;
		const useSameBorderAsBackground =
			!shift.border_color ||
			normalizedBorderColor === normalizedBackgroundColor;

		const borderColor = shift.border_color || shift.background_color;
		const finalBorderColor = useSameBorderAsBackground
			? hexToRgba(borderColor, 0.2)
			: borderColor;

		return (
			<li
				key={item.id}
				title={`${shiftname}`}
				className="shift-item shift-item-intervals"
				style={{
					// backgroundColor: shift.background_color && hexToRgba(shift.background_color, 0.3),
					backgroundColor: shift.background_color,
					borderColor: finalBorderColor,
					color: getContrastColor(shift.background_color),
					cursor: props.hasPrivileges ? "grab" : "default",
					borderRadius: "4px",
				}}
				draggable={props.hasPrivileges}
				onDragStart={(e) => {
					if (!props.hasPrivileges) {
						e.preventDefault(); // Cancela el drag completamente
						return;
					}

					setIsDragging(true);
					onShiftDragStart(e, plan);
				}}
				onDragEnd={(e) => {
					setIsDragging(false);
					// Limpiar atributo de debugging
					e.target.removeAttribute("data-drag-plan");
				}}
				onClick={(e) => onShiftItemDayClick(props, row, index, e)}
				onMouseEnter={() => {
					if (!isDragging && props.hasPrivileges) {
						setTimeout(() => setHoveredAssignmentId(key), 5);
						//setHoveredAssignmentId(key)
					}
				}}
				onMouseLeave={() => {
					if (!isDragging && props.hasPrivileges && !props.isAddingShift) {
						// setTimeout(() => setHoveredAssignmentId(null), 5);
						setHoveredAssignmentId(null);
					}
				}}
			>
				<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
					{/* {item.locked && <RxLockClosed className="plan-icon icon-locked" style={{color: "black"}} size={16} />} */}
					{item.locked && (
						<RxLockClosed className="plan-icon icon-locked" size={16} />
					)}
					{item.time_intervals_from_override_time_registrations && (
						<ImClock className="plan-icon icon-clock" />
					)}
					{hasShiftComments(item) &&
						!showInlineShiftComment(item, shift) &&
						renderShiftCommentIcon("plan-icon icon-comments")}
					{(item.alert_min_rest_hours ||
						item.alert_max_consecutive_working_days) && (
						<OverlayTrigger overlay={tooltip_alerta}>
							<GoAlert className="plan-icon icon-alert" />
						</OverlayTrigger>
					)}
					{editedTimes && <RiPencilFill className="plan-icon icon-edited" />}

					{shift.id === props.default_shift?.id && (
						<span
							style={
								item.alert_min_rest_hours ||
								item.alert_max_consecutive_working_days
									? { marginRight: "10px" }
									: {}
							}
						>
							{i18next.t("shifts.form.typeRest")}
						</span>
					)}
					{shift.id !== props.default_shift?.id &&
						!shouldShowShiftHoursLayout(shift, time_intervals) &&
						(isAbsenceShift(shift.type) ? (
							renderAbsenceShiftLabel(shift, {
								...(item.alert_min_rest_hours ||
								item.alert_max_consecutive_working_days
									? { marginRight: "10px" }
									: {}),
							})
						) : (
							<span
								style={
									item.alert_min_rest_hours ||
									item.alert_max_consecutive_working_days
										? { marginRight: "10px" }
										: {}
								}
							>
								{shift.name}
							</span>
						))}

					{shift.id !== props.default_shift?.id &&
						shouldShowShiftHoursLayout(shift, time_intervals) && (
						<div className="shift-item-content">
							<div className="shift-info-left">
								{time_intervals && time_intervals.length > 0 && (
									<ul className="time-intervals">
										{getShiftIntervalsForDisplay(
											time_intervals,
											shift.background_color,
											shift.type
										)}
									</ul>
								)}
								{isWorkLikeShift(shift.type) &&
									(item.ubications && item.ubications.length > 0 ? (
									<ul className="ubication-list">
										{getShiftUbications(props, sp_item, item, employee)}
									</ul>
								) : (
									<span
										title="Sin ubicación"
										className="shift-ubication substring"
										style={{
											backgroundColor:
												!shift.background_color ||
												isVeryLightColor(shift.background_color)
													? "#000000"
													: shift.background_color,
											borderColor:
												!shift.background_color ||
												isVeryLightColor(shift.background_color)
													? "#000000"
													: shift.border_color
													? shift.border_color
													: shift.background_color,
											color:
												!shift.background_color ||
												isVeryLightColor(shift.background_color)
													? "#FFFFFF"
													: getContrastColor(shift.background_color),
										}}
									>
										Sin ubicación
									</span>
								))}
							</div>

							<div className="shift-info-right">
								{renderShiftTotalWithComment(total, item, shift)}
								{renderShiftBadge(shift, item, time_intervals)}
							</div>
						</div>
					)}
				</div>
				{renderShiftObservationTooltip(
					item,
					hoveredAssignmentId,
					key,
					isDragging
				)}
				{/* Renderizamos el menú de hover solo si este assignment está en hover */}
				{hoveredAssignmentId === key && !isDragging && (
					<PlanShiftHoverMenu
						plan={plan}
						defaultShiftId={props.default_shift?.id}
						onAdd={(update_employee_ids) =>
							props.updateEmployeeIdsAndGetAllAssignments(update_employee_ids)
						}
						onShiftAssigned={props.onShiftAssigned}
						onEdit={() => props.handleEditShift(plan)}
						onDelete={() => props.handleRemoveShift(plan, plan.shift_plan.id)}
					/>
				)}
			</li>
		);
	});

const getShiftUbications = (props, sp_item, item, employee) => {
	const ubications = item.ubications;
	const shift = item.shift;

	if (
		ubications !== undefined &&
		ubications.length > 0 &&
		isWorkLikeShift(shift.type)
	) {
		const plan = {
			employee: employee,
			day: sp_item.day,
			shift_plan: item,
		};
		var lis = ubications.map((ubication, i) => {
			return (
				<li
					key={i}
					title={`${ubication.name} ${
						ubication.description ? "-" + ubication.description : ""
					}`}
					className="ubication-item shift-ubication"
					style={{
						backgroundColor: ubication.background_color
							? ubication.background_color
							: getContrastColor(shift.background_color),
						borderColor: ubication.border_color && ubication.border_color,
						color: ubication.background_color
							? getContrastColor(ubication.background_color)
							: getContrastColor(getContrastColor(shift.background_color)),
					}}
				>
					<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
						{ubication.name}
					</div>
				</li>
			);
		});
		return lis;
	}
};

// celdas plan normal
const getPlanRowContent = (
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell,
	dragTarget,
	setDragTarget,
	employee,
	plans,
	row
) =>
	plans.map((item, i) => {
		const checked = item.checked || false;

		const cut =
			props.day_selection_copy.length > 0 &&
			props.day_selection_copy.find(
				(item) => item.row === row && item.index === i
			);
		const dashed =
			props.day_selection_dashed.length > 0 &&
			props.day_selection_dashed.find(
				(item) => item.row === row && item.index === i
			);

		if (item.active) {
			// vista grid plan (2)
			if (props.plan_grid_view === PLAN_GRID_VIEWS.grid) {
				return (
					<td
						key={i}
						className={`shift-cell shift-cell-grid btn-checkbox 
						${checked ? "active" : ""} 
						${cut ? "cut" : ""} 
						${dashed ? "dashed" : ""}
						${dragOverCell === `${row}-${i}` ? "drag-over" : ""}
						${
							dragTarget?.key === `${row}-${i}` && dragTarget.zone === "top"
								? "drag-over-top"
								: ""
						}
  						${
								dragTarget?.key === `${row}-${i}` &&
								dragTarget.zone === "bottom"
									? "drag-over-bottom"
									: ""
							}
						`}
						onDragOver={(e) =>
							onDragOverSplit(e, row, i, setDragOverCell, setDragTarget)
						}
						onDragLeave={(e) =>
							onDragLeaveSplit(e, setDragOverCell, setDragTarget)
						}
						onDrop={(e) =>
							onShiftsDrop(
								e,
								props,
								row,
								i,
								setIsDragging,
								setDragOverCell,
								dragTarget,
								setDragTarget
							)
						}
						data={{ action: "paste-shift" }}
					>
						{dragTarget?.key === `${row}-${i}` && (
							<div className="split-guides">
								<div
									className={`split-half top ${
										dragTarget?.zone === "top" ? "active" : ""
									}`}
								>
									Mover
								</div>
								<div
									className={`split-half bottom ${
										dragTarget?.zone === "bottom" ? "active" : ""
									}`}
								>
									Copiar
								</div>
							</div>
						)}
						<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
							<input
								type="checkbox"
								id={`day_${row}_${i}`}
								onChange={(e) =>
									props.hasPrivileges && props.handleDaySelection(row, i, e)
								}
								checked={checked}
							/>
							<label
								htmlFor={`day_${row}_${i}`}
								className="btn-check-label"
								draggable={props.hasPrivileges}
								onDragEnter={(e) => props.selectSingleDay(row, i)}
							></label>

							<ul className="shift-list-items">
								{getGridAssignedShifts(
									props,
									hoveredAssignmentId,
									setHoveredAssignmentId,
									isDragging,
									setIsDragging,
									employee,
									item,
									row,
									i
								)}
							</ul>
						</div>
					</td>
				);
			}

			// vista ubicaciones (3)
			// TODO revisar drop en ubicaciones
			if (props.plan_grid_view === PLAN_GRID_VIEWS.ubications) {
				return (
					<td
						key={i}
						className={`shift-cell shift-cell-ubications btn-checkbox 
						${checked ? "active" : ""} 
						${cut ? "cut" : ""} 
						${dashed ? "dashed" : ""}
						${dragOverCell === `${row}-${i}` ? "drag-over" : ""}
						${
							!props.isUbicationListSelected &&
							dragTarget?.key === `${row}-${i}` &&
							dragTarget.zone === "top"
								? "drag-over-top"
								: ""
						}
  						${
								!props.isUbicationListSelected &&
								dragTarget?.key === `${row}-${i}` &&
								dragTarget.zone === "bottom"
									? "drag-over-bottom"
									: ""
							}
						`}
						onDragOver={(e) =>
							onDragOverSplit(e, row, i, setDragOverCell, setDragTarget)
						}
						onDragLeave={(e) =>
							onDragLeaveSplit(e, setDragOverCell, setDragTarget)
						}
						onDrop={(e) =>
							props.isUbicationListSelected
								? onDayUbicationsDrop(e, props, row, i)
								: onShiftsDrop(
										e,
										props,
										row,
										i,
										setIsDragging,
										setDragOverCell,
										dragTarget,
										setDragTarget
								  )
						}
						data={{ action: "paste-shift" }}
					>
						{!props.isUbicationListSelected &&
							dragTarget?.key === `${row}-${i}` && (
								<div className="split-guides">
									<div
										className={`split-half top ${
											dragTarget?.zone === "top" ? "active" : ""
										}`}
									>
										Mover
									</div>
									<div
										className={`split-half bottom ${
											dragTarget?.zone === "bottom" ? "active" : ""
										}`}
									>
										Copiar
									</div>
								</div>
							)}
						<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
							<input
								type="checkbox"
								id={`day_${row}_${i}`}
								onChange={(e) =>
									props.hasPrivileges && props.handleDaySelection(row, i, e)
								}
								checked={checked}
							/>
							<label
								htmlFor={`day_${row}_${i}`}
								className="btn-check-label"
								draggable={props.hasPrivileges}
								onDragEnter={(e) =>
									!props.isUbicationListSelected &&
									props.selectSingleDay(row, i)
								}
							></label>
							<ul className="shift-list-items">
								{getAssignedShiftsWithUbications(
									props,
									hoveredAssignmentId,
									setHoveredAssignmentId,
									isDragging,
									setIsDragging,
									employee,
									item,
									row,
									i
								)}
							</ul>
						</div>
					</td>
				);
			}

			// vista planificación default (1)
			return (
				<td
					key={i}
					className={`shift-cell shift-cell-intervals btn-checkbox 
					${checked ? "active" : ""} 
					${cut ? "cut" : ""} 
					${dashed ? "dashed" : ""}
					${dragOverCell === `${row}-${i}` ? "drag-over" : ""}
					${
						dragTarget?.key === `${row}-${i}` && dragTarget.zone === "top"
							? "drag-over-top"
							: ""
					}
    				${
							dragTarget?.key === `${row}-${i}` && dragTarget.zone === "bottom"
								? "drag-over-bottom"
								: ""
						}
					`}
					onDragOver={(e) =>
						onDragOverSplit(e, row, i, setDragOverCell, setDragTarget)
					}
					onDragLeave={(e) =>
						onDragLeaveSplit(e, setDragOverCell, setDragTarget)
					}
					onDrop={(e) =>
						onShiftsDrop(
							e,
							props,
							row,
							i,
							setIsDragging,
							setDragOverCell,
							dragTarget,
							setDragTarget
						)
					}
					data={{ action: "paste-shift" }}
				>
					{dragTarget?.key === `${row}-${i}` && (
						<div className="split-guides">
							<div
								className={`split-half top ${
									dragTarget?.zone === "top" ? "active" : ""
								}`}
							>
								Mover
							</div>
							<div
								className={`split-half bottom ${
									dragTarget?.zone === "bottom" ? "active" : ""
								}`}
							>
								Copiar
							</div>
						</div>
					)}
					<div className="react-contextmenu-wrapper" onContextMenu={blockContextMenu}>
						<input
							type="checkbox"
							id={`day_${row}_${i}`}
							onChange={(e) =>
								props.hasPrivileges && props.handleDaySelection(row, i, e)
							}
							checked={checked}
						/>
						<label
							htmlFor={`day_${row}_${i}`}
							className="btn-check-label"
							draggable={props.hasPrivileges}
							onDragEnter={(e) => props.selectSingleDay(row, i)}
						></label>

						<ul className="shift-list-items">
							{getDefaultAssignedShifts(
								props,
								hoveredAssignmentId,
								setHoveredAssignmentId,
								isDragging,
								setIsDragging,
								employee,
								item,
								row,
								i
							)}
						</ul>
					</div>
				</td>
			);
		} else {
			return <td key={i} className="shift-cell disabled"></td>;
		}
	});

// ya no se usa
// const onDragOver = (e, row, index, setDragOverCell) => {
// 	e.preventDefault(); // Necesario para permitir el drop

// 	const cellKey = `${row}-${index}`;
// 	//console.log("Drag Over:", cellKey);

// 	setDragOverCell(cellKey); // Establecer la celda resaltada
// };

const onDragOverSplit = (e, row, index, setDragOverCell, setDragTarget) => {
	e.preventDefault();
	const key = `${row}-${index}`;
	setDragOverCell(key);

	// si NO viene de la planificación: no hay copiar/mover, solo feedback básico
	if (!isPlanningDrag(e)) {
		setDragTarget(null);
		try {
			e.dataTransfer.dropEffect = "copy";
		} catch (_) {}
		return;
	}

	const cell = e.currentTarget; // <td>
	const rect = cell.getBoundingClientRect();
	const y = e.clientY - rect.top;
	const zone = y < rect.height / 2 ? "top" : "bottom";

	// nuevo estado con la zona
	setDragTarget((prev) => {
		if (!prev || prev.key !== key || prev.zone !== zone) {
			return { key, zone };
		}
		return prev;
	});
};

const onDragLeaveSplit = (e, setDragOverCell, setDragTarget) => {
	// si el puntero sale de la celda (no entra en un hijo)
	const related = e.relatedTarget;
	if (!e.currentTarget.contains(related)) {
		setDragOverCell(null);
		setDragTarget(null);
	}
};

const onShiftsDrop = (
	e,
	props,
	row,
	i,
	setIsDragging = () => {},
	setDragOverCell = () => {},
	dragTarget = null,
	setDragTarget = () => {}
) => {
	e.preventDefault();
	e.stopPropagation();

	setIsDragging(false);
	setDragOverCell(null);
	const zone =
		dragTarget && dragTarget.key === `${row}-${i}` ? dragTarget.zone : "top";
	setDragTarget(null);

	const dropModeFromZone = zone === "bottom" ? "copy" : "move";
	const dropModeWithModifier =
		(e.ctrlKey || e.altKey || e.metaKey) && dropModeFromZone === "move"
			? "copy"
			: dropModeFromZone;

	const shiftPlan = getInternalShiftPlanFromDropEvent(e);

	if (shiftPlan) {
		if (dropModeWithModifier === "copy") {
			props.handleCopyPasteShift(shiftPlan, row, i);
			return;
		}

		props.handleMoveShift(shiftPlan, row, i);
		return;
	}

	props.handleDayMenuClick(e, {
		action: "paste-shift",
		row,
		index: i,
	});
};

const isPlanningDrag = (e) => isInternalShiftPlanDrag(e);

const onShiftDragStart = (e, plan) => {
	// Verificar que el plan tiene la estructura esperada
	if (!plan || !plan.shift_plan) {
		e.preventDefault();
		return;
	}

	try {
		const planData = JSON.stringify(plan);

		// Usar múltiples formatos para asegurar compatibilidad
		e.dataTransfer.setData("shift", planData);
		e.dataTransfer.setData("text/plain", planData);
		e.dataTransfer.setData("application/json", planData);
		e.dataTransfer.setData("application/x-shift-plan", "1");
		e.dataTransfer.effectAllowed = "copyMove";

		// Almacenar también en el elemento para debugging
		e.target.setAttribute("data-drag-plan", planData);
	} catch (error) {
		e.preventDefault();
	}
};

const onDayUbicationsDrop = (e, props, row, i) => {
	props.selectSingleDay(row, i);
	props.handleDayMenuClick(e, { action: "paste-ubication" });
};

const getHourlyPlanRowContent = (
	props,
	employee,
	plans,
	row,
	hoveredSeriesId,
	setHoveredSeriesId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell,
	dragTarget,
	setDragTarget,
	resizePreview,
	setResizePreview,
	suppressShiftClickRef
) =>
	plans.map((day_plan, i) => {
		// vista diaria por horas
		return (
			<Fragment key={i}>
				<td className="shift-cell shift-cell-hours-main"> {/* dia */} </td>
				{getHourlyPlanRowContentList(
					props,
					employee,
					day_plan,
					row,
					i,
					hoveredSeriesId,
					setHoveredSeriesId,
					isDragging,
					setIsDragging,
					dragOverCell,
					setDragOverCell,
					dragTarget,
					setDragTarget,
					resizePreview,
					setResizePreview,
					suppressShiftClickRef
				)}
			</Fragment>
		);
	});

/**
 * Duración (horas) del periodo de trabajo con isFirst/isLast en la columna-hora.
 * En multi-periodo label/badge miran ESTE periodo, no la suma de todos
 * (si no, un tramo ≤1h sigue mostrando texto porque el total es 8h).
 */
const getIntervalsForHourLabel = (time_intervals, shift) => {
	const workIntervals = (time_intervals || []).filter(
		(iv) => iv.time_type?.work_time
	);
	if (workIntervals.length) return workIntervals;
	if (isVacationOrAbsenceShift(shift?.type)) {
		return getDisplayIntervals(time_intervals, shift.type);
	}
	return workIntervals;
};

const getWorkPeriodDurationHoursAtHourEdge = (
	hourNumber,
	time_intervals,
	edge /* "start" | "end" */,
	shift
) => {
	const labelIntervals = getIntervalsForHourLabel(time_intervals, shift);

	for (const interval of labelIntervals) {
		const localStart = convertFromUTC(interval.start);
		const localEnd = convertFromUTC(interval.end);
		if (!localStart || !localEnd) continue;

		const overlap = getHourBlockOverlapWithLocalInterval(
			hourNumber,
			localStart,
			localEnd
		);
		const matches =
			edge === "start" ? overlap?.isFirst : overlap?.isLast;
		if (matches) {
			return getLocalIntervalDurationMinutes(localStart, localEnd) / 60;
		}
	}

	return 0;
};

const getSeriesText = (hour, employee, day_plan, resizePreview = null) => {
	// Buscar el primer turno que cumple las condiciones para mostrar el label
	for (const item of hour.hour_assignments) {
		const hour_assignment = item.assignment;
		const shift = hour_assignment.shift;

		const raw_time_intervals = getAssignmentRawIntervals(hour_assignment);
		const time_intervals = fixStartEndIntervals(raw_time_intervals);

		if (!item.pinta) {
			continue;
		}

		const { isFirst, isAvailability, start_offset } = item;

		const dayPreview = getPreviewForPlanDay(resizePreview, day_plan.day);
		const previewForLabel = dayPreview
			? {
					...resizePreview,
					previewStart: dayPreview.previewStart,
					previewEnd: dayPreview.previewEnd,
					previewIntervals: dayPreview.intervals,
				}
			: resizePreview;

		const isPreviewingThis =
			resizePreview?.assignmentId === hour_assignment.id &&
			!!dayPreview &&
			previewForLabel.previewStart &&
			previewForLabel.previewEnd;

		// Disponibilidad no lleva label en reposo, pero al arrastrar el usuario
		// necesita ver el rango horario resultante.
		if (isAvailability && !isPreviewingThis) {
			continue;
		}

		let leftPosition = `${start_offset * 100}%`;
		let timeRange = "";
		// Ancho del label = duración del periodo en unidades de celda-hora.
		// El contenedor es la td; % se mide respecto a su ancho.
		let periodHours = 0;

		if (isPreviewingThis) {
			const overlap = getHourBlockOverlapForResizePreview(
				hour.hour,
				previewForLabel,
				isAvailability
			);
			if (!overlap?.isFirst) {
				continue;
			}

			// Duración del periodo que empieza en esta celda (no la suma de todos).
			if (previewForLabel.previewIntervals?.length) {
				const periodAtCell = filterPreviewIntervalsByKind(
					previewForLabel.previewIntervals,
					isAvailability
				).find((iv) => {
					const periodOverlap = getHourBlockOverlapWithLocalInterval(
						hour.hour,
						iv.start,
						iv.end
					);
					return periodOverlap?.isFirst;
				});
				if (periodAtCell) {
					periodHours =
						getLocalIntervalDurationMinutes(
							periodAtCell.start,
							periodAtCell.end
						) / 60;
					timeRange = formatResizePreviewLabel(
						periodAtCell.start,
						periodAtCell.end
					);
				}
			} else {
				periodHours =
					getLocalIntervalDurationMinutes(
						previewForLabel.previewStart,
						previewForLabel.previewEnd
					) / 60;
				timeRange = formatResizePreviewLabel(
					previewForLabel.previewStart,
					previewForLabel.previewEnd
				);
			}

			// Solo si el periodo dura más de 1h
			if (periodHours <= 1) {
				continue;
			}

			leftPosition = `${overlap.start_offset * 100}%`;
		} else if (!isFirst) {
			continue;
		} else {
			periodHours = getWorkPeriodDurationHoursAtHourEdge(
				hour.hour,
				time_intervals,
				"start",
				shift
			);
			if (!(periodHours > 1)) {
				continue;
			}
		}

		const backgroundColor = shift.background_color;
		const color = getContrastColor(shift.background_color);

		if (!timeRange) {
			const hourStr = String(hour.hour).padStart(2, "0");
			const blockStart = moment(`${hourStr}:00`, "HH:mm");
			const blockEnd = moment(blockStart).add(1, "hour");
			const workIntervals = getIntervalsForHourLabel(time_intervals, shift);
			const formatIntervalLabel = (iv) =>
				`${convertFromUTC(iv.start)} - ${convertFromUTC(iv.end)}`;

			for (const interval of workIntervals) {
				const ivStart = moment(convertFromUTC(interval.start), "HH:mm");
				let ivEnd = moment(convertFromUTC(interval.end), "HH:mm");
				const ivEndStr = convertFromUTC(interval.end);
				if (ivEndStr === "00:00") {
					ivEnd = moment("24:00", "HH:mm");
				} else if (ivEnd.isSameOrBefore(ivStart)) {
					ivEnd = moment("24:00", "HH:mm");
				}
				const overlapStart = moment.max(blockStart, ivStart);
				const overlapEnd = moment.min(blockEnd, ivEnd);
				if (overlapStart.isBefore(overlapEnd)) {
					timeRange = formatIntervalLabel(interval);
					break;
				}
			}

			if (!timeRange) {
				timeRange = workIntervals.map(formatIntervalLabel).join(", ");
			}
		}

		// -14px: translateX(10px) del label + margen derecho (borde/asa).
		const maxWidth = `calc(${periodHours * 100}% - 14px)`;

		return {
			color,
			backgroundColor,
			leftPosition,
			maxWidth,
			shiftType: shift.name,
			timeRange,
			title: `Turno ${shift.name}${timeRange ? ` · ${timeRange}` : ""}`,
		};
	}

	return null;
};

const getHourlyPlanRowContentList = (
	props,
	employee,
	day_plan,
	row,
	index,
	hoveredSeriesId,
	setHoveredSeriesId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell,
	dragTarget,
	setDragTarget,
	resizePreview,
	setResizePreview,
	suppressShiftClickRef
) => {
	const isPreviewEmployee =
		resizePreview &&
		(resizePreview.employeeId == null ||
			resizePreview.employeeId === employee.id);
	const dayPreview = getPreviewForPlanDay(resizePreview, day_plan.day);
		const isPreviewDayForEmployee = isPreviewEmployee && !!dayPreview;

	const hoursForRow = isPreviewDayForEmployee
		? mergeDayHoursForResizePreview(
				day_plan.hours,
				dayPreview?.previewStart || resizePreview.previewStart,
				dayPreview?.previewEnd || resizePreview.previewEnd
		  ).map((hour) =>
				enrichHourForResizePreview(hour, day_plan, resizePreview)
		  )
		: day_plan.hours;

	return (hoursForRow || []).map((hour, i) => {
		const hourAssignments = dropRestWhenShiftPaintsHour(
			hour.hour_assignments,
			props.default_shift?.id
		);
		const hourForPaint =
			hourAssignments === hour.hour_assignments
				? hour
				: { ...hour, hour_assignments: hourAssignments };
		const noWorkAssignment = hourForPaint.hour_assignments[0]?.assignment;
		const shift_no_trabajo =
			hourForPaint.hour_assignments.length == 1 &&
			noWorkAssignment &&
			!isWorkLikeShift(noWorkAssignment.shift.type) &&
			!shouldShowShiftHoursLayout(
				noWorkAssignment.shift,
				fixStartEndIntervals(getAssignmentRawIntervals(noWorkAssignment))
			)
				? noWorkAssignment.shift
				: undefined;

		if (!day_plan.active) {
			return <td key={i} className="shift-cell disabled"></td>;
		}

		if (shift_no_trabajo) {
			let shift = shift_no_trabajo;
			// si no es turno de trabajo se muestra pero no se puede editar
			const shiftname = shift.description
				? shift.name + " - " + shift.description
				: shift.name;
			return (
				<td
					className="shift-cell shift-cell-hours"
					key={i}
					title={`${shiftname}`}
					data-plan-day={day_plan.day}
					data-hour={hour.hour}
					data-employee-id={employee.id}
					style={{
						backgroundColor: shift.background_color && shift.background_color,
					}}
					onDragOver={(e) =>
						onDragOverSplit(e, row, i, setDragOverCell, setDragTarget)
					}
					onDragLeave={(e) =>
						onDragLeaveSplit(e, setDragOverCell, setDragTarget)
					}
					onDrop={(e) =>
						onShiftsDrop(
							e,
							props,
							row,
							index, // día del plan (no índice de columna-hora)
							setIsDragging,
							setDragOverCell,
							dragTarget,
							setDragTarget
						)
					}
					data={{ action: "paste-shift" }}
				>
					<span
						className="shift-item"
						style={{
							backgroundColor: shift.background_color && shift.background_color,
							borderColor: shift.border_color
								? shift.border_color
								: shift.background_color || undefined,
							color: getContrastColor(shift.background_color),
						}}
					>
						{shift.name.substring(0, 3)}
					</span>
				</td>
			);
		} else if (hourForPaint.pinta || hourForPaint.hour_assignments.some((ha) => ha.pinta)) {
			// si hay horas trabajadas se pintan en los diferentes turnos (editables)

			// Generar seriesText para el label del turno
			const seriesText = getSeriesText(
				hourForPaint,
				employee,
				day_plan,
				resizePreview
			);
			const hasPostMidnightOverflow = hourForPaint.hour_assignments?.some(
				(ha) => (ha.post_midnight_minutes || 0) > 0 && ha.isLast
			);
			const isResizingThisHour = hourForPaint.hour_assignments?.some(
				(ha) =>
					resizePreview?.assignmentId === ha.assignment?.id &&
					!resizePreview?.sticky
			);

			return (
				<td
					className={`shift-cell shift-cell-hours btn-checkbox${
						hasPostMidnightOverflow ? " shift-cell-post-midnight" : ""
					}${isResizingThisHour ? " shift-cell-resizing" : ""}`}
					data-plan-day={day_plan.day}
					data-hour={hour.hour}
					data-employee-id={employee.id}
					onDragOver={(e) =>
						onDragOverSplit(e, row, i, setDragOverCell, setDragTarget)
					}
					onDragLeave={(e) =>
						onDragLeaveSplit(e, setDragOverCell, setDragTarget)
					}
					onDrop={(e) =>
						onShiftsDrop(
							e,
							props,
							row,
							index, // día del plan (no índice de columna-hora)
							setIsDragging,
							setDragOverCell,
							dragTarget,
							setDragTarget
						)
					}
					key={i}
					style={{ position: "relative" }}
				>
					<ul className="shift-list-items shift-list-items-hours">
						{getHourlyAssignedShifts(
							props,
							employee,
							day_plan,
							hourForPaint,
							hoveredSeriesId,
							setHoveredSeriesId,
							seriesText,
							resizePreview,
							setResizePreview,
							suppressShiftClickRef
						)}
					</ul>
					{seriesText && (
						<div
							className="shift-content-series"
							title={seriesText.title}
							style={{
								color: seriesText.color,
								left: seriesText.leftPosition,
								maxWidth: seriesText.maxWidth,
							}}
						>
							<div className="shift-type">Turno {seriesText.shiftType}</div>
							<div className="shift-time">{seriesText.timeRange}</div>
						</div>
					)}
				</td>
			);
		}
		return (
			// celda vacía
			<td
				className="shift-cell shift-cell-hours"
				data-plan-day={day_plan.day}
				data-hour={hour.hour}
				data-employee-id={employee.id}
				onDragOver={(e) =>
					onDragOverSplit(e, row, i, setDragOverCell, setDragTarget)
				}
				onDragLeave={(e) => onDragLeaveSplit(e, setDragOverCell, setDragTarget)}
				onDrop={(e) =>
					onShiftsDrop(
						e,
						props,
						row,
						index, // día del plan (no índice de columna-hora)
						setIsDragging,
						setDragOverCell,
						dragTarget,
						setDragTarget
					)
				}
				key={i}
			></td>
		);
	});
};

const getHourlyAssignedShifts = (
	props,
	employee,
	day_plan,
	hour,
	hoveredSeriesId,
	setHoveredSeriesId,
	seriesText = null,
	resizePreview = null,
	setResizePreview = null,
	suppressShiftClickRef = null
) =>
	hour.hour_assignments.map((item) => {
		const hour_assignment = item.assignment;
		const hour_count = item.count; // Mantener el count original para el ancho del turno
		const shift = hour_assignment.shift;

		// Detectar turnos de 24 horas para el hover
		const raw_time_intervals = getAssignmentRawIntervals(hour_assignment);
		const is24HourShift =
			raw_time_intervals &&
			raw_time_intervals.some(
				(interval) =>
					!interval.time_type.work_time && interval.start === interval.end
			);

		// Calcular la duración total del turno en horas
		const time_intervals = fixStartEndIntervals(raw_time_intervals);
		const total_seconds = time_intervals
			? calculateWorkTime(time_intervals)
			: 0;
		const total_hours = total_seconds / 3600; // Convertir segundos a horas
		const shiftname = shift.description
			? shift.name + " - " + shift.description
			: shift.name;

		if (!item.pinta) {
			return null;
		}

		const { isFirst, isLast, isAvailability, start_offset, post_midnight_minutes = 0 } = item;
		// hour_count (paint_count) ya incluye el overflow post-medianoche cuando el bloque 23h
		// se extiende hasta 24:MM; no sumar post_midnight_minutes otra vez (p. ej. 125% → 150%).
		let effectiveHourCount = hour_count;
		let effectiveStartOffset = start_offset;
		let effectiveIsFirst = isFirst;
		let effectiveIsLast = isLast;

		const isResizingThis =
			resizePreview?.assignmentId === hour_assignment.id;
		const isMovePreview = resizePreview?.edge === HOURLY_MOVE_EDGE;
		const dayPreview = getPreviewForPlanDay(resizePreview, day_plan.day);
		const previewForThisDay = dayPreview
			? {
					...resizePreview,
					previewStart: dayPreview.previewStart,
					previewEnd: dayPreview.previewEnd,
					previewIntervals: dayPreview.intervals,
				}
			: null;
		if (isResizingThis && resizePreview.previewByDayOffset) {
			if (!previewForThisDay) {
				return null;
			}
			const overlap = getHourBlockOverlapForResizePreview(
				hour.hour,
				previewForThisDay,
				isAvailability
			);
			if (!overlap) {
				return null;
			}
			effectiveHourCount = overlap.count;
			effectiveStartOffset = overlap.start_offset;
			effectiveIsFirst = overlap.isFirst;
			effectiveIsLast = overlap.isLast;

			// Si el preview se extiende fuera de las celdas ya pintadas, el
			// segmento extremo hace overflow para cubrir el hueco en vivo.
			const hourNum = Number(hour.hour);
			const previewIntervalsForSegment = filterPreviewIntervalsByKind(
				previewForThisDay.previewIntervals,
				isAvailability
			);
			const intervalCoveringCell = previewIntervalsForSegment.find(
				(interval) =>
					getHourBlockOverlapWithLocalInterval(
						hour.hour,
						interval.start,
						interval.end
					)
			);
			const draggedInterval =
				resizePreview.previewIntervals?.[resizePreview.intervalIndex ?? 0];
			// Al mover se desplazan todos los periodos, así que el periodo que
			// manda en esta celda es el que la cubre, no el del asa arrastrada.
			// En resize el asa puede pertenecer a otro tipo de periodo (turnos
			// mixtos trabajo + disponibilidad); en ese caso tampoco aplica.
			const resizingInterval =
				!isMovePreview &&
				draggedInterval &&
				!!draggedInterval.isAvailability === !!isAvailability
					? draggedInterval
					: intervalCoveringCell;

			const hasLaterSegmentForInterval = resizingInterval
				? day_plan.hours?.some(
						(h) =>
							Number(h.hour) > hourNum &&
							getHourBlockOverlapWithLocalInterval(
								h.hour,
								resizingInterval.start,
								resizingInterval.end
							)
				  )
				: day_plan.hours?.some(
						(h) =>
							Number(h.hour) > hourNum &&
							h.hour_assignments?.some(
								(ha) =>
									ha.pinta &&
									ha.assignment?.id === hour_assignment.id &&
									getHourBlockOverlapForResizePreview(
										h.hour,
										previewForThisDay,
										isAvailability
									)
							)
				  );

			const hasEarlierSegmentForInterval = resizingInterval
				? day_plan.hours?.some(
						(h) =>
							Number(h.hour) < hourNum &&
							getHourBlockOverlapWithLocalInterval(
								h.hour,
								resizingInterval.start,
								resizingInterval.end
							)
				  )
				: day_plan.hours?.some(
						(h) =>
							Number(h.hour) < hourNum &&
							h.hour_assignments?.some(
								(ha) =>
									ha.pinta &&
									ha.assignment?.id === hour_assignment.id &&
									getHourBlockOverlapForResizePreview(
										h.hour,
										previewForThisDay,
										isAvailability
									)
							)
				  );

			const previewEndForOverflow = resizingInterval
				? resizingInterval.end
				: previewForThisDay.previewEnd;
			const previewStartForOverflow = resizingInterval
				? resizingInterval.start
				: previewForThisDay.previewStart;

			// Al mover, el bloque puede desbordar por ambos lados a la vez.
			const canOverflowEnd =
				isMovePreview || resizePreview.edge === "end";
			const canOverflowStart =
				isMovePreview || resizePreview.edge === "start";

			if (canOverflowEnd && !hasLaterSegmentForInterval) {
				const blockEndMin = (hourNum + 1) * 60;
				const previewEndMin = (() => {
					const [eh, em] = previewEndForOverflow.split(":").map(Number);
					return eh * 60 + em;
				})();
				if (previewEndMin > blockEndMin) {
					// Si la hora siguiente ya tiene solape en el preview, esa celda
					// pinta el tramo; no duplicar con overflow (+15′ parecía +30′).
					const nextHourOverlap = getHourBlockOverlapWithLocalInterval(
						hourNum + 1,
						previewStartForOverflow,
						previewEndForOverflow
					);
					if (!nextHourOverlap) {
						effectiveHourCount += (previewEndMin - blockEndMin) / 60;
						effectiveIsLast = true;
					}
				}
			}

			if (canOverflowStart && !hasEarlierSegmentForInterval) {
				const blockStartMin = hourNum * 60;
				const previewStartMin = (() => {
					const [sh, sm] = previewStartForOverflow
						.split(":")
						.map(Number);
					return sh * 60 + sm;
				})();
				if (previewStartMin < blockStartMin) {
					const prevHourOverlap = getHourBlockOverlapWithLocalInterval(
						hourNum - 1,
						previewStartForOverflow,
						previewEndForOverflow
					);
					if (!prevHourOverlap) {
						const extra = (blockStartMin - previewStartMin) / 60;
						effectiveStartOffset = -extra;
						effectiveHourCount += extra;
						effectiveIsFirst = true;
					}
				}
			}
		} else if (isResizingThis && resizePreview?.deltaMinutes) {
			// Fallback si no hay envelope previsto
			const deltaHours = resizePreview.deltaMinutes / 60;
			if (resizePreview.edge === "end" && effectiveIsLast) {
				effectiveHourCount = hour_count + deltaHours;
			}
			if (resizePreview.edge === "start" && effectiveIsFirst) {
				effectiveStartOffset = start_offset + deltaHours;
				effectiveHourCount = hour_count - deltaHours;
			}
		}

		// Determinar si este turno está siendo hovereado (misma serie)
		const isHovered = hoveredSeriesId === hour_assignment.id;
		const showHoveredStyle =
			isHovered && (!isResizingThis || !!resizePreview?.sticky);
		const showResizingStyle =
			isResizingThis &&
			!!resizePreview &&
			!resizePreview.pendingCommit &&
			!resizePreview.sticky;

		// Calcular estilos con resaltado si está hovereado.
		// T8H y similares llegan sin background_color → transparentes sobre la
		// rejilla blanca; hay que forzar blanco + borde gris para ver el periodo.
		const rawBackgroundColor = shift.background_color;
		const baseBackgroundColor = resolveShiftBackgroundColor(rawBackgroundColor);
		const isLightShift = isLightColor(rawBackgroundColor);
		const baseBorderColor = isLightShift
			? LIGHT_PERIOD_BORDER
			: shift.border_color || baseBackgroundColor;
		const baseColor = getContrastColor(baseBackgroundColor);
		// Oscurecido opaco (no overlay translúcido): los segmentos se solapan
		// y un inset rgba se apilaría en las juntas.
		const hoverBackgroundColor = showHoveredStyle
			? darkenColor(baseBackgroundColor, isLightShift ? 0.2 : 0.14)
			: baseBackgroundColor;
		const hoverBorderColor = isLightShift
			? showHoveredStyle
				? LIGHT_PERIOD_BORDER_HOVER
				: LIGHT_PERIOD_BORDER
			: showHoveredStyle
				? darkenColor(baseBorderColor, 0.14)
				: baseBorderColor;

		// Determinar si es un bloque único (es tanto start como end)
		const isSingleBlock = effectiveIsFirst && effectiveIsLast;
		const isMiddleBlock = !effectiveIsFirst && !effectiveIsLast;
		const isLastBlock = effectiveIsLast && !effectiveIsFirst;

		const baseStyles = {
			backgroundColor: hoverBackgroundColor,
			borderColor: hoverBorderColor,
			color: baseColor,
		};

		const borderWidth = isLightShift ? LIGHT_PERIOD_BORDER_WIDTH : 1;

		// Configurar bordes según el tipo de bloque
		if (isMiddleBlock) {
			// Bloque intermedio: solo bordes superior e inferior
			baseStyles.borderTop = `${borderWidth}px solid ${hoverBorderColor}`;
			baseStyles.borderBottom = `${borderWidth}px solid ${hoverBorderColor}`;
			baseStyles.borderLeft = "none";
			baseStyles.borderRight = "none";
		} else {
			// Otros bloques: borde completo (laterales solo en start/end del periodo)
			baseStyles.border = `${borderWidth}px solid ${hoverBorderColor}`;
		}

		// Calcular el ancho: misma geometría de solape para trabajo, disponibilidad y 24h.
		let finalWidth;
		const baseWidth = `calc(${effectiveHourCount * 100}% - 4px)`;
		const enlargedWidth = `calc(${effectiveHourCount * 100}% + 4px)`;
		const lastBlockWidth = `calc(${effectiveHourCount * 100}% - 2px)`;

		if (isSingleBlock) {
			finalWidth = baseWidth;
		} else if (isMiddleBlock) {
			finalWidth = enlargedWidth;
		} else if (isLastBlock) {
			finalWidth = lastBlockWidth;
		} else {
			finalWidth = enlargedWidth;
		}

		// Badge de función según duración del PERIODO (no suma de todos):
		// - Periodo ≤ 1h: sin label → badge desde 0.75h
		// - Periodo > 1h: con label → badge desde 2h
		// - Trozo día siguiente (overnight): solo esa duración; ocultar si < 2h
		const overnightNextDaySeconds = hour_assignment.time_intervals_overnight?.length
			? calculateIntervalsDuration(
					fixStartEndIntervals(hour_assignment.time_intervals_overnight)
			  )
			: 0;
		const isOvernightNextDayPiece = overnightNextDaySeconds > 0;

		let showFunctionBadge =
			effectiveIsLast && !isAvailability && hour_assignment.function;

		if (showFunctionBadge) {
			if (isOvernightNextDayPiece) {
				showFunctionBadge = overnightNextDaySeconds / 3600 >= 2;
			} else {
				let periodHours = 0;
				if (isResizingThis && resizePreview?.previewIntervals?.length) {
					const periodAtEnd = filterPreviewIntervalsByKind(
						resizePreview.previewIntervals,
						isAvailability
					).find((iv) => {
						const periodOverlap = getHourBlockOverlapWithLocalInterval(
							hour.hour,
							iv.start,
							iv.end
						);
						return periodOverlap?.isLast;
					});
					if (periodAtEnd) {
						periodHours =
							getLocalIntervalDurationMinutes(
								periodAtEnd.start,
								periodAtEnd.end
							) / 60;
					}
				}
				if (!periodHours) {
					periodHours = getWorkPeriodDurationHoursAtHourEdge(
						hour.hour,
						time_intervals,
						"end",
						shift
					);
				}
				if (!periodHours) {
					periodHours = total_hours;
				}
				const hasSeriesLabel = periodHours > 1;
				const minHoursForBadge = hasSeriesLabel ? 2 : 0.75;
				showFunctionBadge = periodHours >= minHoursForBadge;
			}
		}

		/* Puente visual entre días: trozo previo a medianoche (sin time_intervals_overnight) / trozo del día siguiente (copia overnight). */
		const bridgesMidnightToNextDay =
			hour_assignment.is_overnight &&
			effectiveIsLast &&
			!(
				hour_assignment.time_intervals_overnight &&
				hour_assignment.time_intervals_overnight.length > 0
			);
		const bridgesMidnightFromPrevDay =
			hour_assignment.time_intervals_overnight &&
			hour_assignment.time_intervals_overnight.length > 0 &&
			effectiveIsFirst;

		const canResize =
			props.hasPrivileges &&
			isResizeableHourlyAssignment(hour_assignment, isWorkLikeShift) &&
			typeof props.handleResizeHourlyShift === "function";

		const canMove = canResize;

		const showStartResizeHandle = canResize && effectiveIsFirst;
		const showEndResizeHandle = canResize && effectiveIsLast;

		const originDay =
			hour_assignment.span_origin_day ||
			(hour_assignment.time_intervals_overnight?.length
				? moment(day_plan.day, "YYYY-MM-DD")
						.add(-1, "days")
						.format("YYYY-MM-DD")
				: day_plan.day);

		const startShiftDrag = (event, edge) => {
			const isMove = edge === HOURLY_MOVE_EDGE;
			if (isMove ? !canMove : !canResize) return;
			if (!setResizePreview) return;
			if (event.button != null && event.button !== 0) return;

			// En move el pointerdown también es un click normal (abrir modal):
			// no apagar el hover hasta que el arrastre produzca preview.
			if (!isMove) {
				setHoveredSeriesId?.(null);
			}

			const hourCell = event.currentTarget.closest("td.shift-cell-hours");
			if (!raw_time_intervals?.length || !hourCell) return;

			const intervalsForResize = isMove
				? raw_time_intervals
				: (() => {
						if (!isAvailability) return raw_time_intervals;
						const availabilityOnly = raw_time_intervals.filter(
							isAvailabilityTimeInterval
						);
						return availabilityOnly.length
							? availabilityOnly
							: raw_time_intervals;
				  })();

			const intervalIndexInSegment = isMove
				? 0
				: getIntervalIndexForResizeHandle(
						hour.hour,
						intervalsForResize,
						edge
				  );
			if (intervalIndexInSegment < 0) return;

			let intervalIndex = intervalIndexInSegment;
			if (
				!isMove &&
				intervalsForResize !== raw_time_intervals &&
				intervalsForResize[intervalIndexInSegment]
			) {
				const target = intervalsForResize[intervalIndexInSegment];
				const fullIdx = raw_time_intervals.findIndex(
					(iv) =>
						iv === target ||
						(target?.id != null &&
							!Number.isNaN(target.id) &&
							iv.id === target.id) ||
						(iv.start === target.start && iv.end === target.end)
				);
				if (fullIdx >= 0) {
					intervalIndex = fullIdx;
				}
			}

			const handlers = buildResizePointerHandlers({
				edge,
				hourCellEl: hourCell,
				intervals: raw_time_intervals,
				intervalIndex,
				originDay,
				employeeId: employee.id,
				onPreview: (preview) => {
					setHoveredSeriesId?.(null);
					setResizePreview({
						assignmentId: hour_assignment.id,
						employeeId: employee.id,
						assignmentSnapshot: hour_assignment,
						day: originDay,
						...preview,
					});
				},
				onCommit: async (preview) => {
					if (suppressShiftClickRef) {
						suppressShiftClickRef.current = true;
					}
					setHoveredSeriesId?.(null);
					setResizePreview({
						assignmentId: hour_assignment.id,
						employeeId: employee.id,
						assignmentSnapshot: hour_assignment,
						day: originDay,
						pendingCommit: true,
						...preview,
					});

					try {
						const result = await props.handleResizeHourlyShift({
							employee,
							day: originDay,
							shift_plan: hour_assignment,
							edge: preview.edge,
							deltaMinutes: preview.deltaMinutes,
							intervalIndex: preview.intervalIndex,
							dayShift: preview.dayShift,
							nextIntervals: preview.nextIntervals,
						});
						if (!result) {
							setResizePreview(null);
						} else if (result === "optimistic") {
							// Geometría ya materializada en plan.hours; no hace falta sticky.
							setResizePreview(null);
						}
						// "pending-refresh": se limpia al actualizar plan_assignments
					} catch {
						setResizePreview(null);
					}

					window.setTimeout(() => {
						if (suppressShiftClickRef) {
							suppressShiftClickRef.current = false;
						}
					}, 0);
				},
				onCancel: () => {
					setHoveredSeriesId?.(null);
					setResizePreview(null);
				},
			});

			handlers?.start(event);
		};

		return (
			<li
				key={hour_assignment.id}
				title={`${shiftname}`}
				className={`shift-item
				${effectiveIsFirst ? "shift-start" : ""}
				${effectiveIsLast ? "shift-end" : ""}
				${isAvailability ? "shift-availability" : ""}
				${bridgesMidnightToNextDay ? "shift-bridge-next-day" : ""}
				${bridgesMidnightFromPrevDay ? "shift-bridge-prev-day" : ""}
				${showHoveredStyle ? "shift-hovered" : ""}
				${is24HourShift ? "shift-24h" : ""}
				${canMove ? "shift-movable" : ""}
				${showResizingStyle ? "shift-resizing" : ""}`}
				style={{
					...baseStyles,
					width: finalWidth,
					left: `${effectiveStartOffset * 100}%`,
					// transition: 'all 0.2s ease-in-out',
				}}
				onPointerDown={(e) => startShiftDrag(e, HOURLY_MOVE_EDGE)}
				onMouseEnter={() => setHoveredSeriesId(hour_assignment.id)}
				onMouseLeave={() => setHoveredSeriesId(null)}
				onClick={() => {
					if (suppressShiftClickRef?.current) {
						return;
					}
					props.handleEditShift({
						employee,
						day: day_plan.day,
						shift_plan: hour_assignment,
					});
				}}
				onContextMenu={blockContextMenu}
			>
				{showStartResizeHandle && (
					<span
						className="shift-resize-handle shift-resize-handle-start"
						onPointerDown={(e) => startShiftDrag(e, "start")}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
						title="Ajustar inicio (±15 min)"
					/>
				)}
				{showEndResizeHandle && (
					<span
						className="shift-resize-handle shift-resize-handle-end"
						onPointerDown={(e) => startShiftDrag(e, "end")}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
						}}
						title="Ajustar fin (±15 min)"
					/>
				)}
				{showFunctionBadge && (
					<span
						title={hour_assignment.function.name}
						className="shift-function-hourly substring"
						style={{
							backgroundColor:
								hour_assignment.function.background_color || "#000000",
							borderColor:
								hour_assignment.function.background_color || "#000000",
							color: hour_assignment.function.background_color
								? getContrastColor(hour_assignment.function.background_color)
								: "#FFFFFF",
						}}
					>
						{truncatePlanFunctionLabel(hour_assignment.function.name, 5)}
					</span>
				)}
			</li>
		);
	});

const resizePreviewAffectsEmployee = (preview, employeeId) => {
	if (!preview?.previewStart || !preview?.previewEnd) {
		return false;
	}
	if (preview.employeeId == null) {
		return true;
	}
	return preview.employeeId === employeeId;
};

const assignmentContainsShiftId = (assignment, shiftPlanId) => {
	if (shiftPlanId == null) {
		return false;
	}
	for (const plan of assignment?.plans || []) {
		for (const item of plan.assignments || []) {
			if (item.id === shiftPlanId) {
				return true;
			}
		}
	}
	return false;
};

const hoverKeyAffectsRow = (hoverKey, rowIndex, assignment) => {
	if (hoverKey == null) {
		return false;
	}
	const keyStr = String(hoverKey);
	if (keyStr.startsWith(`empty-${rowIndex}-`)) {
		return true;
	}
	for (const plan of assignment?.plans || []) {
		for (const item of plan.assignments || []) {
			if (item.id === hoverKey) {
				return true;
			}
		}
	}
	return false;
};

const dragCellAffectsRow = (cellKey, rowIndex) => {
	if (!cellKey) {
		return false;
	}
	const [row] = String(cellKey).split("-");
	return Number(row) === rowIndex;
};

const workingHoursListEqual = (a, b) => {
	if (a === b) {
		return true;
	}
	if (!a || !b || a.length !== b.length) {
		return false;
	}
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}
	return true;
};

const planTablePrivilegedPropsEqual = (prev, next) =>
	prev.hasPrivileges === next.hasPrivileges &&
	prev.isAddingShift === next.isAddingShift &&
	prev.plan_days === next.plan_days;

function PlanTableEmployeeRowInner({
	rowIndex,
	assignment,
	planGridView,
	workingHoursList,
	resizePreview,
	setResizePreview,
	suppressShiftClickRef,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	hoveredSeriesId,
	setHoveredSeriesId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell,
	dragTarget,
	setDragTarget,
	tableProps,
	groupColor,
	sectionIndex = 0,
}) {
	const employee = assignment.employee;
	const funct =
		employee.employee_with_function &&
		employee.employee_with_function.function
			? employee.employee_with_function.function.name
			: "";
	const plans = assignment.plans;

	let text_ubication = "";

	if (
		employee.employee_with_ubication &&
		employee.employee_with_ubication.ubication
	) {
		const ubication = employee.employee_with_ubication.ubication;
		text_ubication = <>en {ubication.name}</>;
	}

	const employee_name = employee.name + " " + employee.surname;
	const sectionVars = groupColor
		? getMultigroupSectionVars({ color: groupColor }, sectionIndex)
		: undefined;

	return (
		<tr className={groupColor ? "multigroup-employee-row" : undefined} style={sectionVars}>
			<th
				className="main-col th-selectable"
				onClick={() => tableProps.handleRowSelection(rowIndex)}
				style={{ zIndex: 30 }}
			>
				<div className="th-wrap">
					<div className="employee-main">
						<EmployeeLink
							employee={employee}
							className="employee-link-avatar"
							title={employee_name}
							onDrawerClose={tableProps.onEmployeeDrawerClose}
						>
							<Avatar
								userId={employee.id}
								className="avatar-xxs"
								alt={employee_name}
							/>
						</EmployeeLink>
						<div>
							<EmployeeLink
								employee={employee}
								className="employee-link-name substring"
								title={employee_name}
								onDrawerClose={tableProps.onEmployeeDrawerClose}
							>
								{employee_name}
							</EmployeeLink>

							<span className="employee-info">
								<span title={funct} className="employee-function substring">
									{funct}
								</span>
								<span
									title={text_ubication}
									className="employee-ubication substring"
								>
									{text_ubication}
								</span>
							</span>
						</div>
					</div>
				</div>
			</th>

			{planGridView === PLAN_GRID_VIEWS.hours
				? getHourlyPlanRowContent(
						tableProps,
						employee,
						plans,
						rowIndex,
						hoveredSeriesId,
						setHoveredSeriesId,
						isDragging,
						setIsDragging,
						dragOverCell,
						setDragOverCell,
						dragTarget,
						setDragTarget,
						resizePreview,
						setResizePreview,
						suppressShiftClickRef
				  )
				: getPlanRowContent(
						tableProps,
						hoveredAssignmentId,
						setHoveredAssignmentId,
						isDragging,
						setIsDragging,
						dragOverCell,
						setDragOverCell,
						dragTarget,
						setDragTarget,
						employee,
						plans,
						rowIndex
				  )}
		</tr>
	);
}

const planTableEmployeeRowPropsAreEqual = (prev, next) => {
	if (prev.assignment !== next.assignment) {
		return false;
	}
	if (prev.rowIndex !== next.rowIndex) {
		return false;
	}
	if (prev.groupColor !== next.groupColor) {
		return false;
	}
	if (prev.sectionIndex !== next.sectionIndex) {
		return false;
	}
	if (prev.planGridView !== next.planGridView) {
		return false;
	}
	if (!workingHoursListEqual(prev.workingHoursList, next.workingHoursList)) {
		return false;
	}
	if (!planTablePrivilegedPropsEqual(prev.tableProps, next.tableProps)) {
		return false;
	}
	if (
		rowSelectionSignature(prev.tableProps.day_selection, prev.rowIndex) !==
		rowSelectionSignature(next.tableProps.day_selection, next.rowIndex)
	) {
		return false;
	}
	if (prev.isDragging !== next.isDragging) {
		return false;
	}

	const employeeId = prev.assignment.employee.id;

	if (prev.hoveredSeriesId !== next.hoveredSeriesId) {
		const prevHit = assignmentContainsShiftId(
			prev.assignment,
			prev.hoveredSeriesId
		);
		const nextHit = assignmentContainsShiftId(
			next.assignment,
			next.hoveredSeriesId
		);
		if (prevHit || nextHit) {
			return false;
		}
	}

	if (prev.hoveredAssignmentId !== next.hoveredAssignmentId) {
		const prevHit = hoverKeyAffectsRow(
			prev.hoveredAssignmentId,
			prev.rowIndex,
			prev.assignment
		);
		const nextHit = hoverKeyAffectsRow(
			next.hoveredAssignmentId,
			next.rowIndex,
			next.assignment
		);
		if (prevHit || nextHit) {
			return false;
		}
	}

	if (prev.dragOverCell !== next.dragOverCell) {
		const prevHit = dragCellAffectsRow(prev.dragOverCell, prev.rowIndex);
		const nextHit = dragCellAffectsRow(next.dragOverCell, next.rowIndex);
		if (prevHit || nextHit) {
			return false;
		}
	}

	if (prev.dragTarget !== next.dragTarget) {
		const prevHit = dragCellAffectsRow(prev.dragTarget?.key, prev.rowIndex);
		const nextHit = dragCellAffectsRow(next.dragTarget?.key, next.rowIndex);
		if (prevHit || nextHit) {
			return false;
		}
	}

	if (prev.resizePreview !== next.resizePreview) {
		const prevHit = resizePreviewAffectsEmployee(
			prev.resizePreview,
			employeeId
		);
		const nextHit = resizePreviewAffectsEmployee(
			next.resizePreview,
			employeeId
		);
		if (prevHit || nextHit) {
			return false;
		}
	}

	return true;
};

const PlanTableEmployeeRow = memo(
	PlanTableEmployeeRowInner,
	planTableEmployeeRowPropsAreEqual
);

const getPlanRows = (
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell,
	dragTarget,
	setDragTarget,
	hoveredSeriesId,
	setHoveredSeriesId,
	resizePreview,
	setResizePreview,
	suppressShiftClickRef
) =>
	props.plan_assignments.map((assignment, i) => (
		<PlanTableEmployeeRow
			key={assignment.employee?.id ?? i}
			rowIndex={i}
			assignment={assignment}
			planGridView={props.plan_grid_view}
			workingHoursList={props.working_hours_list}
			resizePreview={resizePreview}
			setResizePreview={setResizePreview}
			suppressShiftClickRef={suppressShiftClickRef}
			hoveredAssignmentId={hoveredAssignmentId}
			setHoveredAssignmentId={setHoveredAssignmentId}
			hoveredSeriesId={hoveredSeriesId}
			setHoveredSeriesId={setHoveredSeriesId}
			isDragging={isDragging}
			setIsDragging={setIsDragging}
			dragOverCell={dragOverCell}
			setDragOverCell={setDragOverCell}
			dragTarget={dragTarget}
			setDragTarget={setDragTarget}
			tableProps={props}
		/>
	));

const getMultigroupEmptyDayCells = (dayCount) =>
	dayCount > 0 ? (
		<td
			key="fill"
			colSpan={dayCount}
			className="shift-cell multigroup-section-fill"
		/>
	) : null;

const getMultigroupPlanSections = (
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell,
	dragTarget,
	setDragTarget,
	hoveredSeriesId,
	setHoveredSeriesId,
	resizePreview,
	setResizePreview,
	suppressShiftClickRef
) => {
	const sections = props.multigroupSections || [];
	const dayCount =
		props.plan_days?.length ||
		props.plan_assignments?.[0]?.plans?.length ||
		0;

	return sections.map((section, sectionIndex) => {
		const groupId = section.group.id;
		const sectionVars = getMultigroupSectionVars(section.group, sectionIndex);
		const groupColor = sectionVars["--mg-color"];
		const sectionClass = section.collapsed
			? "multigroup-section is-collapsed"
			: "multigroup-section";

		return (
			<tbody
				key={`mg-section-${groupId}`}
				className={sectionClass}
				style={sectionVars}
			>
				<tr className="multigroup-section-header">
					<th className="main-col">
						<div className="th-wrap">
							<button
								type="button"
								className="multigroup-section-toggle"
								aria-expanded={!section.collapsed}
								title={section.group.name}
								onClick={() =>
									props.onToggleMultigroupSection &&
									props.onToggleMultigroupSection(groupId)
								}
							>
								<span className="multigroup-collapse-icon" aria-hidden="true">
									<FiChevronDown />
								</span>
								<span className="multigroup-section-name">
									{section.group.name}
								</span>
							</button>
						</div>
					</th>
					{getMultigroupEmptyDayCells(dayCount)}
				</tr>
				{section.rows.map((row) => (
					<PlanTableEmployeeRow
						key={`mg-${groupId}-${row.assignment.employee?.id ?? row.sourceRowIndex}`}
						rowIndex={row.sourceRowIndex}
						assignment={row.assignment}
						planGridView={props.plan_grid_view}
						workingHoursList={props.working_hours_list}
						resizePreview={resizePreview}
						setResizePreview={setResizePreview}
						suppressShiftClickRef={suppressShiftClickRef}
						hoveredAssignmentId={hoveredAssignmentId}
						setHoveredAssignmentId={setHoveredAssignmentId}
						hoveredSeriesId={hoveredSeriesId}
						setHoveredSeriesId={setHoveredSeriesId}
						isDragging={isDragging}
						setIsDragging={setIsDragging}
						dragOverCell={dragOverCell}
						setDragOverCell={setDragOverCell}
						dragTarget={dragTarget}
						setDragTarget={setDragTarget}
						tableProps={props}
						groupColor={groupColor}
						sectionIndex={sectionIndex}
					/>
				))}
				<tr className="multigroup-section-subtotal">
					<th className="main-col">
						<div className="th-wrap">
							{i18next.t("plan.multigroup.subtotal", {
								name: section.group.name,
							})}
						</div>
					</th>
					{getMultigroupEmptyDayCells(dayCount)}
				</tr>
			</tbody>
		);
	});
};

const shouldKeepShiftHoverOpen = (currentTarget, relatedTarget) => {
	if (!(relatedTarget instanceof Node)) return false;
	if (currentTarget.contains(relatedTarget)) return true;
	return (
		relatedTarget instanceof Element &&
		!!relatedTarget.closest(".shift-hover-menu-container")
	);
};

const getPlanRowsByUbication = (
	ubicationGroups,
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging
) => {
	const days = props.plan_assignments?.[0]?.plans.map((p) => p.day) || [];

	return ubicationGroups.map((group, row) => {
		const ubication = group.ubication;

		return (
			<tr key={ubication.id}>
				<th className="main-col">
					<div className="th-wrap">
						<div className="ubication-main">
							<span
								className="bolita"
								style={{
									backgroundColor:
										ubication.background_color && ubication.background_color,
								}}
							></span>
							<span
								className="ubication-link-name substring"
								title={ubication.name}
							>
								{ubication.name}
							</span>
						</div>
					</div>
				</th>

				{days.map((day, i) => {
					const shifts = group.days[day] || [];

					return (
						<td
							key={`day_${row}_${i}`}
							className="shift-cell shift-cell-ubication-grouped"
						>
							<ul className="shift-list-items">
								{shifts
									.filter(
										({ assignment }) =>
											assignment.shift.id !== props.default_shift?.id
									)
									.map(({ assignment, employee }) => {
										const key =
											assignment.id !== -1
												? assignment.id
												: `empty-${employee.id}-${day}`;
										const shift = assignment.shift;
										const funcion = assignment.function;
										const raw_time_intervals =
											getAssignmentRawIntervals(assignment, DAY_CARD_INTERVALS);
										const time_intervals =
											fixStartEndIntervals(raw_time_intervals);
										const plan = {
											employee,
											day,
											shift_plan: assignment,
										};

										// Normalizar colores para comparación (lowercase y sin espacios)
										const normalizedBorderColor = shift.border_color
											? shift.border_color.toLowerCase().trim()
											: null;
										const normalizedBackgroundColor = shift.background_color
											? shift.background_color.toLowerCase().trim()
											: null;
										const useSameBorderAsBackground =
											!shift.border_color ||
											normalizedBorderColor === normalizedBackgroundColor;

										const borderColor =
											shift.border_color || shift.background_color;
										const finalBorderColor = useSameBorderAsBackground
											? hexToRgba(borderColor, 0.2)
											: borderColor;

										return (
											<li
												key={key}
												className="shift-item shift-item-intervals"
												style={{
													backgroundColor: shift.background_color,
													borderColor: finalBorderColor,
													color: getContrastColor(shift.background_color),
													cursor: props.hasPrivileges
														? "grab"
														: "default",
													borderRadius: "4px",
												}}
												draggable={props.hasPrivileges}
												onDragStart={(e) => {
													if (!props.hasPrivileges) {
														e.preventDefault();
														return;
													}
													setIsDragging(true);
													onShiftDragStart(e, plan);
												}}
												onDragEnd={(e) => {
													setIsDragging(false);
													e.target.removeAttribute("data-drag-plan");
												}}
												onMouseEnter={() => {
													if (
														!isDragging &&
														props.hasPrivileges
													) {
														setHoveredAssignmentId(key);
													}
												}}
												onMouseLeave={(e) => {
													if (
														!isDragging &&
														props.hasPrivileges &&
														!props.isAddingShift &&
														!shouldKeepShiftHoverOpen(
															e.currentTarget,
															e.relatedTarget
														)
													) {
														setHoveredAssignmentId(null);
													}
												}}
											>
												<div
													className="react-contextmenu-wrapper"
													onContextMenu={blockContextMenu}
												>
													<div className="shift-item-content">
														<div className="shift-info-left">
															<strong className="employee-name substring">
																{employee.name} {employee.surname}
															</strong>

															{time_intervals &&
																time_intervals.length > 0 && (
																	<ul className="time-intervals">
																		{getShiftIntervalsForDisplay(
																			time_intervals,
																			shift.background_color,
																			shift.type
																		)}
																	</ul>
																)}

															{funcion ? (
																<span
																	title={funcion.name}
																	className="shift-function substring"
																	style={{
																		backgroundColor: funcion.background_color
																			? funcion.background_color
																			: !shift.background_color ||
																			  isVeryLightColor(
																					shift.background_color
																			  )
																			? "#000000"
																			: shift.background_color,
																		borderColor: funcion.background_color
																			? funcion.background_color
																			: !shift.background_color ||
																			  isVeryLightColor(
																					shift.background_color
																			  )
																			? "#000000"
																			: shift.border_color
																			? shift.border_color
																			: shift.background_color,
																		color: funcion.background_color
																			? getContrastColor(
																					funcion.background_color
																			  )
																			: !shift.background_color ||
																			  isVeryLightColor(
																					shift.background_color
																			  )
																			? "#FFFFFF"
																			: getContrastColor(
																					shift.background_color
																			  ),
																	}}
																>
																	{truncatePlanFunctionLabel(
																		funcion.name
																	)}
																</span>
															) : (
																<span
																	className="shift-function substring"
																	style={{
																		backgroundColor:
																			!shift.background_color ||
																			isVeryLightColor(
																				shift.background_color
																			)
																				? "#000000"
																				: shift.background_color,
																		borderColor:
																			!shift.background_color ||
																			isVeryLightColor(
																				shift.background_color
																			)
																				? "#000000"
																				: shift.border_color
																				? shift.border_color
																				: shift.background_color,
																		color:
																			!shift.background_color ||
																			isVeryLightColor(
																				shift.background_color
																			)
																				? "#FFFFFF"
																				: getContrastColor(
																						shift.background_color
																				  ),
																	}}
																>
																	Sin función
																</span>
															)}
														</div>

														<div className="shift-info-right">
															{renderShiftBadge(
																shift,
																assignment,
																time_intervals
															)}
														</div>
													</div>
												</div>

												{renderShiftObservationTooltip(
													assignment,
													hoveredAssignmentId,
													key,
													isDragging
												)}
												{hoveredAssignmentId === key &&
													!isDragging && (
														<PlanShiftHoverMenu
															plan={plan}
															defaultShiftId={
																props.default_shift?.id
															}
															onAdd={(update_employee_ids) =>
																props.updateEmployeeIdsAndGetAllAssignments(
																	update_employee_ids
																)
															}
															onShiftAssigned={props.onShiftAssigned}
															onEdit={() =>
																props.handleEditShift(plan)
															}
															onDelete={() =>
																props.handleRemoveShift(
																	plan,
																	plan.shift_plan.id
																)
															}
														/>
													)}
											</li>
										);
									})}
							</ul>
						</td>
					);
				})}
			</tr>
		);
	});
};

const groupAssignmentsByUbicationAndDay = (plan_assignments) => {
	const result = {};

	plan_assignments.forEach(({ employee, plans }) => {
		plans.forEach((plan) => {
			plan.assignments.forEach((assignment) => {
				const day = plan.day;
				const ubications =
					assignment.ubications?.length > 0
						? assignment.ubications
						: employee.employee_with_ubication?.ubication
						? [employee.employee_with_ubication.ubication]
						: [];

				ubications.forEach((ub) => {
					if (!result[ub.id]) {
						result[ub.id] = {
							ubication: ub,
							days: {},
						};
					}
					if (!result[ub.id].days[day]) {
						result[ub.id].days[day] = [];
					}
					result[ub.id].days[day].push({
						assignment,
						employee,
					});
				});
			});
		});
	});

	// Convertir el objeto a array para renderizar
	return Object.values(result);
};

// ── Vista "Turnos" ───────────────────────────────────────────────────────────

/** Minutos desde medianoche del inicio más temprano (hora local, orden creciente). */
const getShiftStartMinutes = (shift) => {
	const intervals = shift?.time_intervals;
	if (!intervals?.length) return Number.MAX_SAFE_INTEGER;

	let minStart = Number.MAX_SAFE_INTEGER;
	intervals.forEach((interval) => {
		const localStart = convertFromUTC(interval.start);
		if (!localStart) return;
		const [hoursRaw, minutesRaw] = localStart.split(":");
		const hours = Number(hoursRaw);
		const minutes = Number(minutesRaw) || 0;
		if (Number.isNaN(hours)) return;
		minStart = Math.min(minStart, hours * 60 + minutes);
	});

	return minStart;
};

/** Rango por tipo: trabajo/automático arriba (por hora), ausencias/vacaciones abajo. */
const getShiftTypeRank = (shift) => {
	const normalized = normalizeShiftType(shift?.type);
	if (normalized === shiftType.work || normalized === shiftType.automatic) {
		return 0;
	}
	if (normalized === shiftType.absence || normalized === shiftType.vacation) {
		return 2;
	}
	if (normalized === shiftType.rest) return 1;
	return 1;
};

/** Firma estable de intervalos para agrupar variantes del mismo turno (p. ej. fracciones IA). */
const getIntervalsGroupSignature = (intervals) => {
	if (!intervals?.length) return "";
	return intervals
		.map((interval) => `${interval.start}|${interval.end}`)
		.join(";");
};

/** True si la asignación tiene horarios efectivos distintos del template del turno. */
const assignmentUsesDistinctIntervals = (assignment, shift) => {
	const rawIntervals = getAssignmentRawIntervals(assignment, DAY_CARD_INTERVALS);
	const intervals = fixStartEndIntervals(rawIntervals);
	if (!intervals?.length) return false;

	const templateIntervals = fixStartEndIntervals(shift?.time_intervals);
	const assignmentSig = getIntervalsGroupSignature(intervals);
	const templateSig = getIntervalsGroupSignature(templateIntervals);

	if (assignmentSig && templateSig && assignmentSig !== templateSig) {
		return true;
	}

	// Turno Automático creado por IA: intervalos solo en la asignación.
	if (isAutomaticShift(shift?.type) && assignment.time_intervals_from_manual) {
		return true;
	}

	return false;
};

const getShiftGroupKey = (shift, assignment) => {
	const rawIntervals = getAssignmentRawIntervals(assignment, DAY_CARD_INTERVALS);
	const intervals = fixStartEndIntervals(rawIntervals);

	if (assignmentUsesDistinctIntervals(assignment, shift) && intervals?.length) {
		return `${shift.id}::${getIntervalsGroupSignature(intervals)}`;
	}
	return String(shift.id);
};

/** Agrupa las asignaciones por turno (y variante de horario) y día. */
const groupAssignmentsByShiftAndDay = (plan_assignments, default_shift) => {
	const result = {};

	plan_assignments.forEach(({ employee, plans }) => {
		plans.forEach((plan) => {
			plan.assignments.forEach((assignment) => {
				const shift = assignment.shift;
				if (!shift) return;
				if (default_shift && shift.id === default_shift.id) return;

				const day = plan.day;
				const groupKey = getShiftGroupKey(shift, assignment);
				const rawIntervals = getAssignmentRawIntervals(assignment, DAY_CARD_INTERVALS);
				const effectiveIntervals = fixStartEndIntervals(rawIntervals);

				if (!result[groupKey]) {
					result[groupKey] = {
						groupKey,
						shift,
						effectiveIntervals,
						days: {},
					};
				}
				if (!result[groupKey].days[day]) {
					result[groupKey].days[day] = [];
				}
				result[groupKey].days[day].push({ assignment, employee });
			});
		});
	});

	return Object.values(result).sort((a, b) => {
		const rankDiff = getShiftTypeRank(a.shift) - getShiftTypeRank(b.shift);
		if (rankDiff !== 0) return rankDiff;

		const startDiff =
			getShiftStartMinutes({
				time_intervals: a.effectiveIntervals || a.shift?.time_intervals,
			}) -
			getShiftStartMinutes({
				time_intervals: b.effectiveIntervals || b.shift?.time_intervals,
			});
		if (startDiff !== 0) return startDiff;

		return (a.shift.name || "").localeCompare(b.shift.name || "");
	});
};

const onShiftsViewDragOver = (e, shiftRowIndex, dayIndex, setDragOverCell) => {
	e.preventDefault();
	if (!isPlanningDrag(e)) return;
	setDragOverCell(`shift-${shiftRowIndex}-${dayIndex}`);
	try {
		e.dataTransfer.dropEffect = "move";
	} catch (_) {}
};

const onShiftsViewDragLeave = (e, setDragOverCell) => {
	const related = e.relatedTarget;
	if (!e.currentTarget.contains(related)) {
		setDragOverCell(null);
	}
};

const onShiftsViewDrop = (
	e,
	props,
	shiftRowIndex,
	dayIndex,
	targetShift,
	targetDay,
	setIsDragging,
	setDragOverCell,
	targetIntervals
) => {
	e.preventDefault();
	e.stopPropagation();

	setIsDragging(false);
	setDragOverCell(null);

	let shiftData = e.dataTransfer.getData("shift");
	if (!shiftData) {
		shiftData = e.dataTransfer.getData("text/plain");
	}
	if (!shiftData) {
		shiftData = e.dataTransfer.getData("application/json");
	}
	if (!shiftData) {
		const draggedElement = document.querySelector("[data-drag-plan]");
		if (draggedElement) {
			shiftData = draggedElement.getAttribute("data-drag-plan");
		}
	}

	if (!shiftData) return;

	try {
		const shiftPlan = JSON.parse(shiftData);
		if (!shiftPlan.shift_plan || !shiftPlan.employee) return;

		// Solo es un no-op si coincide turno, día Y horario. En turnos
		// "Automático" hay varios grupos con el mismo shift.id pero distinto
		// horario (fracciones IA), por lo que mover entre variantes de horario
		// del mismo turno/día SÍ debe reasignar.
		const sourceIntervals = fixStartEndIntervals(
			getAssignmentRawIntervals(shiftPlan.shift_plan, DAY_CARD_INTERVALS)
		);
		const sameSlot =
			shiftPlan.shift_plan.shift?.id === targetShift.id &&
			shiftPlan.day === targetDay &&
			getIntervalsGroupSignature(sourceIntervals) ===
				getIntervalsGroupSignature(targetIntervals);
		if (sameSlot) {
			return;
		}

		props.handleReassignShiftInShiftsView(
			shiftPlan,
			targetShift.id,
			targetDay,
			targetIntervals
		);
	} catch (_) {}
};

const renderPlanLoadSkeletonRows = (skeletonCount, dayCount) => {
	if (skeletonCount <= 0) return null;

	return Array.from({ length: skeletonCount }).map((_, skeletonIndex) => (
		<tr
			key={`plan-row-skeleton-${skeletonIndex}`}
			className={`plan-row-skeleton${
				skeletonIndex === 0 ? " plan-row-skeleton--label" : ""
			}`}
			aria-hidden={skeletonIndex === 0 ? undefined : true}
		>
			<th className="main-col">
				<div className="th-wrap">
					{skeletonIndex === 0 ? (
						<div className="employee-main plan-skeleton-employee">
							<span className="plan-skeleton-block plan-skeleton-avatar" />
							<span className="plan-skeleton-loading-label">
								Cargando más empleados…
							</span>
						</div>
					) : (
						<div className="employee-main plan-skeleton-employee">
							<span className="plan-skeleton-block plan-skeleton-avatar" />
							<span className="plan-skeleton-lines">
								<span className="plan-skeleton-block plan-skeleton-name" />
								<span className="plan-skeleton-block plan-skeleton-meta" />
							</span>
						</div>
					)}
				</div>
			</th>
			{Array.from({ length: dayCount }).map((__, dayIndex) => (
				<td
					key={`plan-row-skeleton-${skeletonIndex}-day-${dayIndex}`}
					className="shift-cell"
				>
					<span className="plan-skeleton-block plan-skeleton-cell" />
				</td>
			))}
		</tr>
	));
};

const getPlanRowsByShift = (
	shiftGroups,
	props,
	hoveredAssignmentId,
	setHoveredAssignmentId,
	isDragging,
	setIsDragging,
	dragOverCell,
	setDragOverCell
) => {
	const days = props.plan_assignments?.[0]?.plans.map((p) => p.day) || [];

	return shiftGroups.map((group, shiftRowIndex) => {
		const shift = group.shift;
		const shiftname = shift.description
			? `${shift.name} - ${shift.description}`
			: shift.name;
		const displayIntervals =
			group.effectiveIntervals?.length
				? group.effectiveIntervals
				: shift.time_intervals;
		const periodsLabel =
			isVacationOrAbsenceShift(shift.type) &&
			!hasPartialDayIntervals(displayIntervals)
				? ""
				: getFirstLastInterval(displayIntervals);

		return (
			<tr key={group.groupKey}>
				<th className="main-col">
					<div className="th-wrap">
						<div className="shift-main">
							<span
								className="bolita"
								style={{
									backgroundColor:
										shift.background_color && shift.background_color,
								}}
							></span>
							<div className="shift-main-info">
								<span className="shift-link-name substring" title={shiftname}>
									{shift.name}
								</span>
								{periodsLabel ? (
									<div className="shift-footer">{periodsLabel}</div>
								) : null}
							</div>
						</div>
					</div>
				</th>

				{days.map((day, i) => {
					const people = group.days[day] || [];
					const cellKey = `shift-${shiftRowIndex}-${i}`;

					return (
						<td
							key={`day_${shift.id}_${i}`}
							className={`shift-cell shift-cell-shifts-grouped${
								dragOverCell === cellKey ? " drag-over" : ""
							}`}
							onDragOver={(e) =>
								onShiftsViewDragOver(
									e,
									shiftRowIndex,
									i,
									setDragOverCell
								)
							}
							onDragLeave={(e) =>
								onShiftsViewDragLeave(e, setDragOverCell)
							}
							onDrop={(e) =>
								onShiftsViewDrop(
									e,
									props,
									shiftRowIndex,
									i,
									shift,
									day,
									setIsDragging,
									setDragOverCell,
									group.effectiveIntervals
								)
							}
						>
							<ul className="shift-list-items plan-person-card-list">
								{people.map(({ assignment, employee }) => {
									const key =
										assignment.id !== -1
											? assignment.id
											: `empty-${employee.id}-${day}`;
									const raw_time_intervals =
										getAssignmentRawIntervals(assignment, DAY_CARD_INTERVALS);
									const time_intervals =
										fixStartEndIntervals(raw_time_intervals);
									const plan = {
										employee,
										day,
										shift_plan: assignment,
									};

									return (
										<li
											key={key}
											className="plan-person-card-item"
											style={{
												cursor: props.hasPrivileges
													? "grab"
													: "default",
											}}
											draggable={props.hasPrivileges}
											onDragStart={(e) => {
												if (!props.hasPrivileges) {
													e.preventDefault();
													return;
												}
												setIsDragging(true);
												onShiftDragStart(e, plan);
											}}
											onDragEnd={(e) => {
												setIsDragging(false);
												e.target.removeAttribute(
													"data-drag-plan"
												);
											}}
											onMouseEnter={() => {
												if (
													!isDragging &&
													props.hasPrivileges
												) {
													setHoveredAssignmentId(key);
												}
											}}
											onMouseLeave={() => {
												if (
													!isDragging &&
													props.hasPrivileges &&
													!props.isAddingShift
												) {
													setHoveredAssignmentId(null);
												}
											}}
										>
											<div
												className="react-contextmenu-wrapper"
												onContextMenu={blockContextMenu}
											>
												<PlanPersonCard
													name={`${employee.name} ${employee.surname || ""}`.trim()}
													functionObj={assignment.function}
													shift={shift}
													badge={renderPersonIaBadge(
														shift,
														assignment,
														time_intervals
													)}
												/>
											</div>

											{renderShiftObservationTooltip(
												assignment,
												hoveredAssignmentId,
												key,
												isDragging
											)}
											{hoveredAssignmentId === key &&
												!isDragging && (
													<PlanShiftHoverMenu
														plan={plan}
														showAdd={false}
														showEdit={false}
														onDelete={() =>
															props.handleRemoveShift(
																plan,
																plan.shift_plan.id
															)
														}
													/>
												)}
										</li>
									);
								})}
							</ul>
						</td>
					);
				})}
			</tr>
		);
	});
};

const PlanTableBody = (props) => {
	const [hoveredAssignmentId, setHoveredAssignmentId] = useState(null);
	const [hoveredSeriesId, setHoveredSeriesId] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [dragOverCell, setDragOverCell] = useState(null);
	const [dragTarget, setDragTarget] = useState(null);
	const resizePreview = props.resizePreview ?? null;
	const setResizePreview = props.setResizePreview;
	const suppressShiftClickRef = useRef(false);
	const hoveredSeriesRafRef = useRef(null);
	const pendingHoveredSeriesRef = useRef(null);

	const updateHoveredSeriesId = useCallback((id) => {
		if (id == null) {
			if (hoveredSeriesRafRef.current != null) {
				cancelAnimationFrame(hoveredSeriesRafRef.current);
				hoveredSeriesRafRef.current = null;
			}
			pendingHoveredSeriesRef.current = null;
			setHoveredSeriesId(null);
			return;
		}

		pendingHoveredSeriesRef.current = id;
		if (hoveredSeriesRafRef.current == null) {
			hoveredSeriesRafRef.current = requestAnimationFrame(() => {
				hoveredSeriesRafRef.current = null;
				setHoveredSeriesId(pendingHoveredSeriesRef.current);
				pendingHoveredSeriesRef.current = null;
			});
		}
	}, []);

	// recoge los datos de planes agrupados por ubicación
	const ubicationGroups = useMemo(() => {
		if (props.plan_grid_view === PLAN_GRID_VIEWS.ubication_grouped) {
			return groupAssignmentsByUbicationAndDay(props.plan_assignments);
		}
		return [];
	}, [props.plan_assignments, props.plan_grid_view]);

	// recoge los datos de planes agrupados por turno
	const shiftGroups = useMemo(() => {
		if (props.plan_grid_view === PLAN_GRID_VIEWS.shifts) {
			return groupAssignmentsByShiftAndDay(
				props.plan_assignments,
				props.default_shift
			);
		}
		return [];
	}, [props.plan_assignments, props.plan_grid_view, props.default_shift]);

	const skeletonCount = Math.max(0, props.plan_pending_skeleton_count || 0);
	const dayCount =
		props.plan_days?.length ||
		props.plan_assignments?.[0]?.plans?.length ||
		0;
	const skeletonRows =
		supportsPlanLoadSkeletons(props.plan_grid_view) &&
		renderPlanLoadSkeletonRows(skeletonCount, dayCount);

	if (props.plan_grid_view === PLAN_GRID_VIEWS.shifts) {
		return (
			<tbody>
				{getPlanRowsByShift(
					shiftGroups,
					props,
					hoveredAssignmentId,
					setHoveredAssignmentId,
					isDragging,
					setIsDragging,
					dragOverCell,
					setDragOverCell
				)}
				{skeletonRows}
			</tbody>
		);
	}

	const isMultigroup =
		props.multigroupSections?.length &&
		props.plan_grid_view === PLAN_GRID_VIEWS.default;

	if (isMultigroup) {
		return (
			<>
				{getMultigroupPlanSections(
					props,
					hoveredAssignmentId,
					setHoveredAssignmentId,
					isDragging,
					setIsDragging,
					dragOverCell,
					setDragOverCell,
					dragTarget,
					setDragTarget,
					hoveredSeriesId,
					updateHoveredSeriesId,
					resizePreview,
					setResizePreview,
					suppressShiftClickRef
				)}
				{skeletonRows ? <tbody>{skeletonRows}</tbody> : null}
			</>
		);
	}

	return (
		<tbody>
			{props.plan_grid_view === PLAN_GRID_VIEWS.ubication_grouped
				? getPlanRowsByUbication(
						ubicationGroups,
						props,
						hoveredAssignmentId,
						setHoveredAssignmentId,
						isDragging,
						setIsDragging,
						dragOverCell,
						setDragOverCell,
						dragTarget,
						setDragTarget
				  )
				: getPlanRows(
						props,
						hoveredAssignmentId,
						setHoveredAssignmentId,
						isDragging,
						setIsDragging,
						dragOverCell,
						setDragOverCell,
						dragTarget,
						setDragTarget,
						hoveredSeriesId,
						updateHoveredSeriesId,
						resizePreview,
						setResizePreview,
						suppressShiftClickRef
				  )}
			{skeletonRows}
		</tbody>
	);
};

export default PlanTableBody;
